import { isValidAddress, toChecksumAddress } from '@ethereumjs/util';
import { addHexPrefix, isNullish } from '@particle-network/auth';
import { EthereumSepolia, ParticleChains, chains } from '@particle-network/chains';
import { iframeWallet } from '@particle-network/iframe-wallet';
import axios from 'axios';
import BigNumber from 'bignumber.js';
import BN from 'bn.js';
import dayjs from 'dayjs';
import { simpleEncode } from 'ethereumjs-abi';
import { AbiCoder } from 'ethers';
import ethjs from 'ethjs-unit';
import { cloneDeep } from 'lodash';
import md5 from 'md5';
import numbro from 'numbro';
import { v4 as uuidv4 } from 'uuid';
import CommonApi from '../apis/common-api';
import connection from '../apis/connection';
import database from '../apis/data/database';
import { defaultTokenIcon, getChainInfo, getChainList, getNativeIcon, getTXUrl } from '../apis/data/index';
import EvmRpc from '../apis/rpc/evm-rpc';
import { DateFormat, EVM_SWAP_NATIVE_ADDDRESS, PioneerChainId, STATIC_PARTICLE_NETWORK, SUPPORTED_TARGET_TOKENS, UniversalApiUrl, UniversalMultiChainModuleAddress, ValidEvmAddress, getGlobalChain, } from '../config';
import { CROSS_USER_OPERATION_CHAIN_STATUS, CROSS_USER_OPERATION_STATUS, CROSS_USER_OPERATION_V2_STATUS, TOKENLIst_SUPPORTED_TOKEN_TYPE, TransactionType, TransactionsType, } from '../types';
import { isChainSupportDID, isValidDID } from '../utils/didUtils';
import { fiatAmountFormat, formWalletAddress, gasFee, getCacheTokenList, getNewEVMChainInfoById, getParticleChainInfo, getTokenIcon, getTokenListCacheKey, parseToAddress, shakeZero, sleep, stripHexPrefix, subString, switchChain, toNonExponential, toWei, tokenAmountFormat, validBitcoinAddress, } from '../utils/index';
import { gasNetworkFee } from '../utils/networkFees';
import { aaOptions } from './aaOptions';
export var TransactionSmartType;
(function (TransactionSmartType) {
    TransactionSmartType["NativeTransfer"] = "native_transfer";
    TransactionSmartType["ERC20_TRANSFER"] = "erc20_transfer";
    TransactionSmartType["ERC20_APPROVE"] = "erc20_approve";
    TransactionSmartType["ERC721_TRANFER"] = "erc721_transfer";
    TransactionSmartType["ERC1155_TRANFER"] = "erc1155_transfer";
    TransactionSmartType["OTHER"] = "other";
})(TransactionSmartType || (TransactionSmartType = {}));
export class EvmService {
    options;
    wallet;
    appId;
    provider;
    projectId;
    clientKey;
    commonApi;
    rpc;
    rpcUrl;
    chainType;
    chainName;
    chainId;
    transactionChainType = '0x0';
    currency = 'USD';
    evmUnit;
    t;
    mockAddress;
    evm1559support;
    web3;
    swapProvider = '';
    fusionProvider = '';
    tokenListKey = '';
    accountAbstraction;
    smartAccount;
    database;
    universalApiUrl = '';
    events;
    constructor(provider, options) {
        this.options = options;
        this.chainType = 'evm';
        this.provider = provider;
        this.chainId = options.chainId;
        this.chainName = options.chainName.toLowerCase();
        this.projectId = options.projectId;
        this.clientKey = options.clientKey;
        this.appId = options.appId;
        this.mockAddress = options.mockAddress;
        this.events = options.events;
        const rpcUrl = this.provider?.rpcUrl || `${process.env.NEXT_PUBLIC_RPC_URL}/evm-chain${process.env.NEXT_PUBLIC_USE_PUBLIC_RPC === 'true' ? '/public' : ''}`;
        this.rpcUrl = rpcUrl + `?chainId=${this.chainId}&projectUuid=${this.projectId}&projectKey=${this.clientKey}`;
        console.log(`rpcUrl: ${this.rpcUrl}`);
        this.rpc = new EvmRpc(connection(this.rpcUrl, this.projectId, this.clientKey));
        this.commonApi = new CommonApi(connection(STATIC_PARTICLE_NETWORK, this.projectId, this.clientKey));
        this.currency = options.currency || 'USD';
        this.evmUnit = getParticleChainInfo(this.chainName, this.chainId).evmUnit;
        this.evm1559support = false;
        this.getEvm1559support();
        this.t = options.t;
        this.database = database;
        if (options.accountAbstraction && this.accountAbstractionChains().includes(Number(this.chainId))) {
            // @ts-ignore
            this.smartAccount = new window.__particle_aa__.SmartAccount(provider, {
                projectId: this.projectId,
                clientKey: this.clientKey,
                appId: this.appId,
                aaOptions,
            });
            this.accountAbstraction = window.__webWalletData.accountAbstraction || {};
            const { name, version = '1.0.0' } = this.accountAbstraction;
            if (name) {
                this.smartAccount.setSmartAccountContract({ name, version });
            }
        }
    }
    /**
     * getTransactionChainType
     */
    accountAbstractionChains = (aaName, aaVersion) => {
        const name = (aaName || this.options.accountAbstraction?.name || 'BICONOMY');
        const version = aaVersion || this.options.accountAbstraction?.version || '1.0.0';
        let ids = (aaOptions.accountContracts[name] || []).find((o) => o.version === version)?.chainIds || [];
        if (!ids.length) {
            ids = (aaOptions.accountContracts[name] || []).find((o) => o.version === '1.0.0')?.chainIds || [];
        }
        return ids;
    };
    enableAccountAbstraction = () => {
        return !isNullish(this.smartAccount);
    };
    async getAddressFromDID(did) {
        if (!isValidDID(did)) {
            throw new Error('Invalid DID');
        }
        if (!isChainSupportDID({ id: Number(this.chainId), name: this.chainName })) {
            throw new Error('Current chain does not support DID');
        }
        return this.rpc.getAddressFromDID(did);
    }
    async getDIDFromAddress(address) {
        if (!isValidAddress(address)) {
            throw new Error('Invalid Address');
        }
        if (!isChainSupportDID({ id: Number(this.chainId), name: this.chainName })) {
            throw new Error('Current chain does not support DID');
        }
        return this.rpc.getDIDFromAddress(address);
    }
    /**
     * https://minijoy.feishu.cn/docx/MkdTdRqBwo7ds2xKD4UcTDsInWh#MhQcdVL4ooSYYoxAFbjcjKJJnkh
     * @param smartAccount
     * @param txs
     * @returns
     */
    createCrossChainUserOperation(smartAccount, txs) {
        return axios
            .post(UniversalApiUrl, {
            jsonrpc: '2.0',
            chainId: this.chainId,
            method: 'universal_createCrossChainUserOperation',
            params: [smartAccount, txs],
        })
            .then((res) => {
            const errorMessage = res.data?.error?.extraData || res.data?.error?.message;
            if (errorMessage) {
                throw new Error(errorMessage);
            }
            else if (!res.data?.result?.userOps?.length) {
                throw new Error('No user operation created');
            }
            return res.data.result || {};
        });
    }
    /**
     * https://minijoy.feishu.cn/docx/MkdTdRqBwo7ds2xKD4UcTDsInWh#ZFMWdvBf6odnINxVpFOcx5CKnug
     * 创建跨链用户操作
     * @param params
     * @returns
     */
    async createGaslessCrossChainUserOperation(params) {
        let { smartAccount, userOps, chainConfig, targetChainId } = params;
        if (!smartAccount) {
            smartAccount = await this.getSmartAccount();
        }
        const method = 'universal_createGaslessCrossChainUserOperation';
        return axios
            .post(UniversalApiUrl + `?method=${method}`, {
            jsonrpc: '2.0',
            chainId: targetChainId || this.chainId,
            method,
            params: [smartAccount, userOps, chainConfig],
        })
            .then((res) => {
            const errorMessage = res?.data?.error?.data || res.data?.error?.extraData || res.data?.error?.message;
            if (errorMessage) {
                throw new Error(errorMessage);
            }
            let list = res.data.result || [];
            const sortType = ['usdg', 'eth', 'bnb', 'btc', 'usdc'];
            list = list.sort((a, b) => {
                return sortType.indexOf(a.type) - sortType.indexOf(b.type);
            });
            return list;
        });
    }
    async getSmartAccount() {
        let eoaAddress = await iframeWallet.ethereum?.request({ method: 'eth_accounts' }).then((res) => {
            return res[0] ?? '';
        });
        if (!eoaAddress) {
            // TODO
            eoaAddress = '0x257C04a36b8E2380deEac8D2A7acdE85d26EFfC2';
        }
        return {
            ...this.accountAbstraction,
            ownerAddress: eoaAddress,
        };
    }
    /**
     * https://minijoy.feishu.cn/docx/MkdTdRqBwo7ds2xKD4UcTDsInWh#GM78dg8iroQ7r1x2YyZc5G0Onyg
     * @returns
     */
    async createLPCrossChainUserOperation(params) {
        let { smartAccount, transaction, supportTokenTypes, tokenChanges, targetChainId } = params;
        if (!smartAccount) {
            smartAccount = await this.getSmartAccount();
        }
        const method = 'universal_createLPCrossChainUserOperation';
        let data = [smartAccount, transaction, supportTokenTypes];
        if (tokenChanges && tokenChanges?.length) {
            data.push(tokenChanges);
        }
        return axios
            .post(UniversalApiUrl + `?chainId=${targetChainId}&method=${method}`, {
            jsonrpc: '2.0',
            chainId: targetChainId,
            method,
            params: data,
        })
            .then((res) => {
            const errorMessage = res.data?.error?.extraData || res.data?.error?.data || res.data?.error?.message;
            if (errorMessage) {
                throw new Error(errorMessage);
            }
            return res.data.result;
        });
    }
    /**
     * https://minijoy.feishu.cn/docx/MkdTdRqBwo7ds2xKD4UcTDsInWh#FrhndmHlwox6EoxoXd1cwqnNnod
     * @param userOps
     * @param cfTurnstileResponse
     * @returns
     */
    async sendLPCrossChainUserOperation(params) {
        if (!params.smartAccount) {
            params.smartAccount = await this.getSmartAccount();
        }
        const { cfTurnstileResponse, targetChainId, ...paramValue } = params;
        const method = 'universal_sendLPCrossChainUserOperation';
        return axios
            .post(UniversalApiUrl + `?method=${method}`, {
            jsonrpc: '2.0',
            chainId: targetChainId || this.chainId,
            method,
            params: [paramValue],
            cfTurnstileResponse,
        })
            .then((res) => {
            const errorMessage = res?.data?.error?.data || res.data?.error?.extraData || res.data?.error?.message;
            if (errorMessage) {
                throw new Error(errorMessage);
            }
            return res.data.result;
        });
    }
    /**
     * https://minijoy.feishu.cn/docx/MkdTdRqBwo7ds2xKD4UcTDsInWh#FOjcdfa0MoMETXxGEaMcveRInnc
     * @param userOps
     * @param cfTurnstileResponse
     * @returns
     */
    sendCrossChainUserOperation(userOps, cfTurnstileResponse) {
        return axios
            .post(UniversalApiUrl, {
            jsonrpc: '2.0',
            chainId: this.chainId,
            method: 'universal_sendCrossChainUserOperation',
            params: [userOps],
            cfTurnstileResponse,
        })
            .then((res) => {
            const errorMessage = res.data?.error?.extraData || res.data?.error?.message;
            if (errorMessage) {
                throw new Error(errorMessage);
            }
            else if (!res.data.result) {
                throw new Error('Send user operation failed');
            }
            return res.data.result;
        });
    }
    /**
     * https://minijoy.feishu.cn/docx/MkdTdRqBwo7ds2xKD4UcTDsInWh#MjcWdAmy2o3FP8xMMdgctVkJn3e
     * @param params
     * @returns
     */
    async getCrossChainUserOperations(params) {
        const method = 'universal_getCrossChainUserOperations';
        return axios
            .post(UniversalApiUrl + `?method=${method}`, {
            jsonrpc: '2.0',
            chainId: EthereumSepolia.id,
            method,
            params: params,
        })
            .then((res) => {
            return res.data.result;
        });
    }
    /**
     * https://minijoy.feishu.cn/docx/MkdTdRqBwo7ds2xKD4UcTDsInWh#BTtOd50AjoZmYIxcMqrcXDF9n1f
     * @param params
     * @returns
     */
    async getLPCrossChainUserOperation(id, type = 'detail') {
        const method = 'universal_getLPCrossChainUserOperation';
        const account = await this.getAddress();
        return axios
            .post(UniversalApiUrl + `?method=${method}`, {
            jsonrpc: '2.0',
            chainId: EthereumSepolia.id,
            method,
            params: [id],
        })
            .then((res) => {
            if (type === 'normal') {
                return res.data.result;
            }
            return this.formatLPCrossChainTransaction(account, res.data.result);
        });
    }
    /**
     * https://minijoy.feishu.cn/docx/MkdTdRqBwo7ds2xKD4UcTDsInWh#PNQDdmTdFogGxvxU0MOcllShnud
     * @param params
     * @returns
     */
    async getLPCrossChainUserOperations(page, limit = 10) {
        const method = 'universal_getLPCrossChainUserOperations';
        let account = await this.getAddress();
        // 获取url上 watch_account
        if (typeof window !== 'undefined' && location.search.includes('_account')) {
            account = location.search.split('_account=')[1].split('&')[0];
        }
        // @ts-ignore
        if (typeof window !== 'undefined' && window._account) {
            // @ts-ignore
            account = window._account;
        }
        const params = [
            {
                // TODO
                // sender: '0x6C6F014e755312cC78564E52a8371AB24a138c5d',
                sender: account,
            },
            {
                page,
                limit,
            },
        ];
        let res = await axios
            .post(UniversalApiUrl + `?method=${method}`, {
            jsonrpc: '2.0',
            chainId: EthereumSepolia.id,
            method,
            params: params,
        })
            .then((res) => {
            return res.data.result;
        });
        let list = (res?.data || []).map(async (item) => {
            return this.formatLPCrossChainTransaction(account, item);
        });
        list = await Promise.all(list);
        return {
            list,
            hasNextPage: res?.hasNextPage,
        };
    }
    /**
     *
     * https://minijoy.feishu.cn/docx/MkdTdRqBwo7ds2xKD4UcTDsInWh#MjcWdAmy2o3FP8xMMdgctVkJn3e
     * @param id
     * @returns
     */
    async getCrossChainUserOperation(id) {
        return axios
            .post(UniversalApiUrl, {
            jsonrpc: '2.0',
            chainId: EthereumSepolia.id,
            method: 'universal_getCrossChainUserOperation',
            params: [id],
        })
            .then((res) => {
            return res.data.result;
        });
    }
    async formatLPCrossChainTransaction(account, transaction) {
        let from = transaction.sender || '';
        const txData = transaction.executionUserOps?.[0]?.txs?.pop?.() || transaction.lendingUserOps?.[0]?.txs?.pop?.() || {};
        let to = txData.to;
        if (transaction.tag === 'purchase_pioneer_nft') {
            from = txData.to || from;
            to = transaction.sender;
        }
        else if (txData.data && SUPPORTED_TARGET_TOKENS.find((item) => item.tokenAddress.toLowerCase() === txData.to.toLowerCase())) {
            try {
                to = parseToAddress(txData.data);
            }
            catch (error) {
                console.log('parseToAddress error', error);
            }
        }
        to = to || from;
        const toChainId = transaction?.lendingUserOps?.[0]?.chainId || transaction?.executionUserOps?.[0]?.chainId;
        const dateValue = dayjs(transaction.createdAt).format('YYYY/MM/DD HH:mm:ss');
        const hash = transaction?.executionUserOps?.[0]?.txHash ?? transaction?.lendingUserOps?.[0]?.txHash;
        let addressValue = '';
        let actionName = '';
        /**
         * 发送金额
         */
        let sendAmount = tokenAmountFormat(transaction?.depositUserOps?.reduce((acc, cur) => {
            const amount = (cur?.tokenChanges || [])?.reduce?.((subAcc, subCur) => {
                return subAcc.plus(new BigNumber(subCur.amount).div(new BigNumber(10).pow(Number(subCur?.token.decimals || 0))));
            }, new BigNumber(0)) || new BigNumber(0);
            return acc.plus(amount);
        }, new BigNumber(0)));
        // if (new BigNumber(sendAmount).lt(0.0000000001)) {
        //   sendAmount = '< 0.0000000001';
        // }
        /**
         * 发送金额 USD
         */
        // const sendAmountUSD = (transaction?.depositUserOps as any[])
        //   ?.reduce((acc, cur) => {
        //     const amountUSD =
        //       ((cur?.tokenChanges as any[]) || [])?.reduce?.((subAcc, subCur) => {
        //         return subAcc.plus(new BigNumber(subCur.amountUSD).div(new BigNumber(10).pow(18)));
        //       }, new BigNumber(0)) || new BigNumber(0);
        //     return acc.plus(amountUSD);
        //   }, new BigNumber(0))
        //   .toString();
        // const swapAmount = tokenAmountFormat(
        //   (transaction?.depositUserOps as any[])?.reduce((acc, cur) => {
        //     const decimals = getNewEVMChainInfoById(Number(cur.chainId))?.nativeCurrency.decimals;
        //     const amount =
        //       ((cur?.txs as any[]) || [])?.reduce?.((subAcc, subCur) => {
        //         return subAcc.plus(new BigNumber(subCur.value || 0).div(new BigNumber(10).pow(Number(decimals || 0))));
        //       }, new BigNumber(0)) || new BigNumber(0);
        //     return acc.plus(amount);
        //   }, new BigNumber(0)),
        //   8
        // );
        const sendChaiId = transaction?.depositUserOps?.[0]?.chainId || transaction?.executionUserOps?.[0]?.chainId || transaction?.lendingUserOps?.[0]?.chainId;
        const sendChain = getNewEVMChainInfoById(sendChaiId);
        const sendSymbol = transaction?.depositUserOps?.[0]?.tokenChanges?.[0]?.token?.type?.toUpperCase() ||
            transaction?.executionUserOps?.[0]?.tokenChanges?.[0]?.token?.type?.toUpperCase() ||
            transaction?.lendingUserOps?.[0]?.tokenChanges?.[0]?.token?.type?.toUpperCase();
        // const sendDecimals = sendChain?.nativeCurrency?.decimals || 18;
        const sendChainIcon = sendChain?.icon || '';
        const sendChainIcons = [...new Set(transaction.depositUserOps?.map((o) => o.chainId))].map((chainId) => {
            const chain = getNewEVMChainInfoById(Number(chainId));
            return {
                chainId: chainId,
                icon: chain?.icon,
                name: chain?.name,
            };
        });
        const isSwap = !transaction?.executionUserOps || transaction?.executionUserOps?.length === 0 || to?.toLowerCase() === from.toLowerCase();
        if ((!isSwap && (!to || to?.toLowerCase() === account.toLowerCase() || transaction.tag === 'purchase_pioneer_nft')) || transaction.tag === 'mint_nft') {
            actionName = 'in';
            addressValue = `${this.t('from')}: ${subString(toChecksumAddress(from))}`;
        }
        else {
            actionName = 'out';
            addressValue = `${this.t('to')}: ${subString(toChecksumAddress(to))}`;
        }
        const statusName = (() => {
            if (transaction?.status === CROSS_USER_OPERATION_V2_STATUS.FINISHED) {
                // 7 显示成功的状态
                return 'confirmed';
            }
            else if ([
                CROSS_USER_OPERATION_V2_STATUS.DEPOSIT_FAILED,
                CROSS_USER_OPERATION_V2_STATUS.EXECUTION_FAILED,
                CROSS_USER_OPERATION_V2_STATUS.REFUND_PENDING,
                CROSS_USER_OPERATION_V2_STATUS.REFUND_SUCCESS,
                CROSS_USER_OPERATION_V2_STATUS.REFUND_FAILED,
                CROSS_USER_OPERATION_V2_STATUS.REFUND_FINISHED,
                CROSS_USER_OPERATION_V2_STATUS.PENNY_FAILED,
            ].includes(transaction?.status)) {
                // 3 6 8 9 10  11 14前端显示失败的状态
                return 'failed';
            }
            else {
                return 'pending';
            }
        })();
        const data = {
            id: transaction._id,
            deserializedData: {
                type: transaction.tag === 'purchase_pioneer_nft'
                    ? TransactionsType.RSS3_NFT_BUY
                    : transaction.tag === 'mint_nft'
                        ? TransactionsType.RSS3_NFT_MINT
                        : transaction.tag === 'checkin'
                            ? TransactionsType.CHECK_IN
                            : isSwap
                                ? TransactionsType.RSS3_SWAP
                                : actionName == 'in'
                                    ? TransactionsType.NATIVE_RECEIVE
                                    : TransactionsType.NATIVE_SEND, // TODO ???
            },
            from,
            to,
            dateValue,
            actionName,
            action: actionName,
            hash,
            badge: sendChainIcon,
            sendChainIcons,
            chainId: sendChaiId,
            addressValue,
            amountChangeNatives: [
                {
                    symbol: sendSymbol,
                },
            ],
            amountChangeList: [
                {
                    amountView: to?.toLowerCase() === account.toLowerCase() ? '+' + sendAmount : actionName === 'in' ? '+' : '-' + sendAmount,
                    symbol: sendSymbol,
                },
            ],
            pending: statusName === 'pending',
        };
        const typeName = this.getTransactionsTypeName(data);
        let detail = {};
        {
            const [tokenChanges, gaslessTokenChanges] = [transaction.depositUserOps, transaction.gaslessUserOps]?.map((userOps) => {
                if (!userOps)
                    return [];
                return Object.entries(cloneDeep(userOps)
                    .map((item) => {
                    if (!Array.isArray(item.tokenChanges)) {
                        return [item.tokenChanges].map((o) => {
                            return { ...o, ...o.token, chainId: item.chainId, txHash: item?.txHash };
                        });
                    }
                    return item.tokenChanges.map((o) => {
                        return { ...o, ...o.token, chainId: item.chainId, txHash: item?.txHash };
                    });
                })
                    .flat(Infinity)
                    .reduce((acc, cur) => {
                    const key = `${cur.tokenAddress}_${cur.chainId}`;
                    acc[key] = acc[key] || [];
                    acc[key].push(cur);
                    return acc;
                }, {})).map((item) => {
                    const [key, list = []] = item;
                    const amount = list
                        ?.reduce((acc, cur) => {
                        return acc.plus(new BigNumber(cur.amount).div(new BigNumber(10).pow(cur.decimals)));
                    }, new BigNumber(0))
                        .toString();
                    const amountUSD = list
                        ?.reduce((acc, cur) => {
                        return acc.plus(new BigNumber(cur.amountUSD));
                    }, new BigNumber(0))
                        .toString();
                    const tokenAddress = list[0].tokenAddress;
                    const chainId = Number(list[0].chainId);
                    return {
                        ...list[0].token,
                        tokenAddress,
                        chainId,
                        amount,
                        amountUSD,
                        txHash: list[0].txHash,
                    };
                });
            });
            const data = tokenChanges?.map((item, index) => {
                return {
                    id: index,
                    name: item?.type,
                    chainName: getNewEVMChainInfoById(item.chainId)?.fullname,
                    amount: item?.amount,
                    value: new BigNumber(item.amountUSD).dividedBy(new BigNumber(10).pow(18)).toString(),
                    useValue: new BigNumber(item.amountUSD).dividedBy(new BigNumber(10).pow(18)).toString(),
                    iconSrc: getTokenIcon(item.type?.toUpperCase()),
                    txHash: item.txHash,
                    chainId: item?.chainId,
                };
            });
            const networkFeeData = gaslessTokenChanges?.map((item, index) => {
                const amount = new BigNumber(item?.amount);
                return {
                    id: index,
                    name: item?.type,
                    chainName: getNewEVMChainInfoById(item.chainId)?.fullname,
                    amount: amount.toString(),
                    value: new BigNumber(item.amountUSD).dividedBy(new BigNumber(10).pow(18)).toString(),
                    useValue: new BigNumber(item.amountUSD).dividedBy(new BigNumber(10).pow(18)).toString(),
                    iconSrc: getTokenIcon(item.type?.toUpperCase()),
                    txHash: item.txHash,
                    chainId: item?.chainId,
                };
            });
            const settlementData = {
                chainName: getNewEVMChainInfoById(transaction.settlementUserOps?.[0]?.chainId)?.fullname,
                txHash: transaction.settlementUserOps?.[0]?.txHash,
                chainId: transaction.settlementUserOps?.[0]?.chainId,
            };
            /**
             * 接收链的图标
             */
            let receiveChainIcons = [];
            if (toChainId) {
                receiveChainIcons = [
                    {
                        icon: getNewEVMChainInfoById(toChainId)?.icon || '',
                        name: getNewEVMChainInfoById(toChainId)?.name || '',
                        chainId: toChainId,
                    },
                ];
            }
            const fromAddress = from;
            const toAddress = to;
            const contacts = (await this.getUserAddress())?.filter((item) => !!item.alias);
            const toTag = '';
            const toNickname = contacts?.find?.((o) => o.userAddress?.toLowerCase() === toAddress?.toLowerCase())?.alias || '';
            const fromNickname = contacts?.find?.((o) => o.userAddress?.toLowerCase() === fromAddress?.toLowerCase())?.alias || '';
            const typeName = '';
            const deserializedType = '';
            detail = {
                data,
                tokenChanges,
                gaslessTokenChanges,
                networkFeeData,
                settlementData,
                statusName,
                executionUserOps: transaction.executionUserOps,
                lendingUserOps: transaction.lendingUserOps,
                toTag,
                toNickname,
                fromNickname,
                typeName,
                fromAddress,
                receiveChainIcons,
                sendChainIcons,
                toAddress,
                deserializedType,
            };
        }
        const result = {
            ...data,
            statusName,
            typeName,
            detail,
        };
        return result;
    }
    /**
     * https://minijoy.feishu.cn/docx/MkdTdRqBwo7ds2xKD4UcTDsInWh#Re0wdNRinomGjnxhrsIcXZSOnld
     * @param params
     * @returns
     */
    async getUniversalTokens(selectedGlobalChain) {
        if (!selectedGlobalChain) {
            return [];
        }
        const method = 'universal_getTokens';
        let account = await this.getAddress();
        if (!account) {
            await sleep(1000);
            account = await this.getAddress();
        }
        if (!account) {
            throw new Error('Please connect wallet,code:100037');
        }
        const params = [account, Object.values(TOKENLIst_SUPPORTED_TOKEN_TYPE)];
        const res = await axios.post(UniversalApiUrl + `?method=${method}`, {
            jsonrpc: '2.0',
            chainId: EthereumSepolia.id,
            method,
            params: params,
        });
        if (res.data.error) {
            throw new Error(res.data.error.extraData ?? res.data.error.message);
        }
        const globalChain = getGlobalChain();
        const list = res?.data?.result || [];
        const tokenPrice = Object.fromEntries(list
            .map((item) => item?.tokenBalances?.[0]?.token)
            .filter((item) => item?.type)
            .map((item) => {
            return [item.type, Number(item.exchangeRateToUSD || 0)];
        }));
        let tokens = list.map((item) => {
            const symbol = item.tokenType?.toUpperCase();
            const decimals = Number(item.tokenBalances?.[0]?.token?.decimals || 6);
            const tokenAddress = item.tokenBalances?.[0]?.token?.tokenAddress;
            const amount = item.tokenBalances
                ?.reduce((prev, current) => {
                return new BigNumber(prev).plus(new BigNumber(current.amount).dividedBy(new BigNumber(10).pow(current.token.decimals)));
            }, new BigNumber(0))
                .toString();
            const chainIcons = [...new Set(item?.tokenBalances.filter((o) => Number(o.amount)))].map((o) => {
                const chainId = o.token?.chainId;
                const { amount, amountUSD } = o;
                const decimals = Number(o.token?.decimals);
                const chain = getNewEVMChainInfoById(Number(chainId));
                return {
                    icon: chain?.icon,
                    name: chain?.name,
                    amountUSD: new BigNumber(amountUSD).dividedBy(new BigNumber(10).pow(18)).toString(),
                    decimals: Number(o.token?.decimals),
                    chainId: Number(chainId),
                    chainName: chain?.fullname,
                    amount: new BigNumber(amount).dividedBy(new BigNumber(10).pow(decimals)).toString(),
                };
            });
            const tokenBalances = new BigNumber(amount).multipliedBy(tokenPrice[item.tokenType] || 0);
            return {
                tokenType: item.tokenType,
                chainId: globalChain.chainId,
                address: symbol === 'ETH' ? 'native' : tokenAddress,
                amount,
                name: symbol,
                symbol,
                decimals,
                image: getTokenIcon(symbol),
                metadata: item,
                unitPrice: tokenPrice[item.tokenType] || 0,
                price: fiatAmountFormat(tokenBalances, 6),
                '24hChange': 0,
                oPrice: Number(fiatAmountFormat(tokenBalances)),
                doubleUnitPrice: fiatAmountFormat(tokenBalances),
                uPrice: tokenPrice[item.tokenType] || 0,
                chains: chainIcons,
            };
        });
        tokens = tokens.sort((a, b) => {
            return Number(b.doubleUnitPrice || 0) - Number(a.doubleUnitPrice || 0);
        });
        return tokens;
    }
    async sendCrossChainTransaction(cfTurnstileResponse, params) {
        let { smartAccount, transactionId, depositUserOps, executionUserOps, gaslessUserOps, settlementUserOps, targetChainId } = params || {};
        if (!smartAccount) {
            smartAccount = await this.getSmartAccount();
        }
        return this.universalContractCall(smartAccount, {
            cfTurnstileResponse,
            transactionId,
            depositUserOps,
            executionUserOps,
            gaslessUserOps,
            settlementUserOps,
            targetChainId,
        }, cfTurnstileResponse, 'v2');
    }
    /**
     * https://minijoy.feishu.cn/docx/MkdTdRqBwo7ds2xKD4UcTDsInWh#TLfmdaRc0oMN1BxjlNvcN91znce
     * @param smartAccount
     * @param userOps
     * @param cfTurnstileResponse
     * @returns
     */
    async universalContractCall(smartAccount, userOps, cfTurnstileResponse, version = 'v1') {
        console.log('>>> universalContractCall params', smartAccount, userOps, cfTurnstileResponse, version);
        const targetChainId = version === 'v2' ? userOps.targetChainId : 0;
        let v2Data = null;
        if (version === 'v2') {
            v2Data = cloneDeep(userOps);
            userOps = [...userOps.depositUserOps, ...userOps.executionUserOps, ...userOps.gaslessUserOps, ...userOps.settlementUserOps].map((item) => {
                return {
                    chainId: Number(item.chainId),
                    userOp: item.userOp,
                    userOpHash: item.userOpHash,
                };
            });
        }
        if (!smartAccount || !userOps?.length) {
            throw new Error('UniversalContractCall error');
        }
        const validUntil = Math.floor(Date.now() / 1000) + 600;
        const validAfter = Math.floor(Date.now() / 1000) - 600;
        const currentChainPublicClient = window.__webWalletData.getPublicClient(this.chainId);
        const unsignedData = (await currentChainPublicClient.request({
            method: 'particle_aa_createMultiChainUnsignedData',
            params: [
                smartAccount,
                {
                    multiChainConfigs: userOps.map((userOp) => ({
                        chainId: Number(userOp.chainId),
                        userOpHash: userOp.userOpHash,
                        validUntil,
                        validAfter,
                    })),
                },
            ],
        }));
        const merkleRoot = unsignedData.merkleRoot;
        if (!merkleRoot || !userOps || !userOps?.length) {
            throw new Error('Invalid params');
        }
        userOps = Array.isArray(userOps) ? userOps : [userOps];
        await window.__webWalletData.walletSwitchChain(this.provider, this.chainId);
        const walletClient = window.__webWalletData.getWalletClient(this.provider);
        // @ts-ignore
        const { ownerAddress } = (await this?.smartAccount?.getAccountConfig?.()) || {};
        if (!ownerAddress) {
            throw new Error('Invalid owner address');
        }
        let signature = '';
        try {
            signature = await walletClient.signMessage({
                account: ownerAddress,
                message: { raw: window.__webWalletData.toBytes(merkleRoot) },
            });
        }
        catch (error) {
            if (error?.details || error?.message) {
                throw new Error(error?.details || error?.message);
            }
            else {
                throw error;
            }
        }
        const abiCoder = AbiCoder.defaultAbiCoder();
        const sendUserOps = [];
        for (const [index, userOp] of userOps.entries()) {
            sendUserOps.push({
                ...userOp.userOp,
                chainId: userOp.chainId,
                signature: abiCoder.encode(['bytes', 'address'], [
                    abiCoder.encode(['uint48', 'uint48', 'bytes32', 'bytes32[]', 'bytes'], [unsignedData.data[index].validUntil, unsignedData.data[index].validAfter, unsignedData.merkleRoot, unsignedData.data[index].merkleProof, signature]),
                    UniversalMultiChainModuleAddress,
                ]),
            });
        }
        let targetTxHash = '';
        if (version === 'v2') {
            const { cfTurnstileResponse, transactionId, depositUserOps, executionUserOps, gaslessUserOps, settlementUserOps } = v2Data;
            [depositUserOps, executionUserOps, gaslessUserOps, settlementUserOps].flat(Infinity).forEach((item, index) => {
                if (!sendUserOps[index]) {
                    throw new Error('Invalid sendUserOps');
                }
                Object.assign(item, sendUserOps[index]);
                item.userOp = sendUserOps[index];
            });
            const params = {
                cfTurnstileResponse,
                transactionId: transactionId,
                depositUserOps: depositUserOps,
                executionUserOps: executionUserOps,
                gaslessUserOps: gaslessUserOps,
                settlementUserOps: settlementUserOps,
                targetChainId,
            };
            let resultStatus = 'pending';
            const sentOps = await this.sendLPCrossChainUserOperation(params);
            let txResult = {};
            let crossChainUserOperationResult = null;
            do {
                crossChainUserOperationResult = await this.getLPCrossChainUserOperation(sentOps._id, 'normal');
                const depositUserOps = crossChainUserOperationResult?.depositUserOps?.map((item) => {
                    return {
                        chainId: item.chainId,
                        txHash: item?.userOpEvent?.txHash || item.txHash,
                    };
                });
                const executionUserOps = crossChainUserOperationResult?.executionUserOps?.map((item) => {
                    return {
                        chainId: item.chainId,
                        txHash: item?.userOpEvent?.txHash || item.txHash,
                    };
                });
                const gaslessUserOps = crossChainUserOperationResult?.gaslessUserOps?.map((item) => {
                    return {
                        chainId: item.chainId,
                        txHash: item?.userOpEvent?.txHash || item.txHash,
                    };
                });
                const settlementUserOps = crossChainUserOperationResult?.settlementUserOps?.map((item) => {
                    return {
                        chainId: item.chainId,
                        txHash: item?.userOpEvent?.txHash || item.txHash,
                    };
                });
                const lendingUserOps = crossChainUserOperationResult?.lendingUserOps?.map((item) => {
                    return {
                        chainId: item.chainId,
                        txHash: item?.userOpEvent?.txHash || item.txHash,
                    };
                });
                resultStatus =
                    crossChainUserOperationResult.status === CROSS_USER_OPERATION_V2_STATUS.FINISHED || executionUserOps?.[0]?.txHash || lendingUserOps?.[0]?.txHash
                        ? // 7  || executionUserOps.txHash || lendingUserOps.txHash   成功
                            'success'
                        : [
                            CROSS_USER_OPERATION_V2_STATUS.DEPOSIT_FAILED,
                            CROSS_USER_OPERATION_V2_STATUS.EXECUTION_FAILED,
                            CROSS_USER_OPERATION_V2_STATUS.REFUND_PENDING,
                            CROSS_USER_OPERATION_V2_STATUS.REFUND_SUCCESS,
                            CROSS_USER_OPERATION_V2_STATUS.REFUND_FAILED,
                            CROSS_USER_OPERATION_V2_STATUS.REFUND_FINISHED,
                            CROSS_USER_OPERATION_V2_STATUS.PENNY_FAILED,
                        ].includes(crossChainUserOperationResult.status)
                            ? // 3 6 8 9 10  11 14 失败
                                'failed'
                            : // 其它的pending
                                'pending';
                await sleep(1000);
                if (resultStatus === 'success') {
                    txResult = {
                        depositUserOps,
                        executionUserOps,
                        gaslessUserOps,
                        settlementUserOps,
                        lendingUserOps,
                    };
                }
            } while (resultStatus === 'pending');
            if (resultStatus === 'failed') {
                throw new Error(crossChainUserOperationResult?.gaslessUserOps?.find((item) => !!item.sendFailedReason)?.sendFailedReason ||
                    crossChainUserOperationResult?.depositUserOps?.find((item) => !!item.sendFailedReason)?.sendFailedReason ||
                    crossChainUserOperationResult?.lendingUserOps?.find((item) => !!item.sendFailedReason)?.sendFailedReason ||
                    crossChainUserOperationResult?.executionUserOps?.find((item) => !!item.sendFailedReason)?.sendFailedReason ||
                    'Transaction failed');
            }
            return txResult;
        }
        else {
            const sentOps = await this.sendCrossChainUserOperation(sendUserOps, cfTurnstileResponse);
            let particleTxHash = '';
            while (!targetTxHash) {
                const userOperation = await this.getCrossChainUserOperation(sentOps._id);
                const particleStatus = userOperation?.particleStatus;
                const targetStatus = userOperation?.targetStatus;
                const status = userOperation?.status;
                const targetSendFailedReason = userOperation?.targetSendFailedReason;
                const particleSendFailedReason = userOperation?.particleSendFailedReason;
                console.log('>>>userOperation', userOperation);
                if (particleSendFailedReason) {
                    throw new Error(particleSendFailedReason);
                }
                if (targetSendFailedReason) {
                    throw new Error(targetSendFailedReason);
                }
                if (particleStatus === CROSS_USER_OPERATION_CHAIN_STATUS.SEND_FAILED ||
                    particleStatus === CROSS_USER_OPERATION_CHAIN_STATUS.CHAIN_FAILED ||
                    targetStatus === CROSS_USER_OPERATION_CHAIN_STATUS.SEND_FAILED ||
                    targetStatus === CROSS_USER_OPERATION_CHAIN_STATUS.CHAIN_FAILED ||
                    status === CROSS_USER_OPERATION_STATUS.PARTICLE_SEND_FAILED ||
                    status === CROSS_USER_OPERATION_STATUS.TARGET_SEND_FAILED) {
                    break;
                }
                else {
                    // if (particleStatus === CROSS_USER_OPERATION_CHAIN_STATUS.CHAIN_SUCCESS && targetStatus === CROSS_USER_OPERATION_CHAIN_STATUS.CHAIN_SUCCESS) {
                    targetTxHash = userOperation?.targetUserOpEvent?.txHash;
                    particleTxHash = userOperation?.particleUserOpEvent?.txHash;
                    // }
                }
                await sleep(1000);
            }
            if (!targetTxHash) {
                throw new Error('Transaction failed');
            }
        }
        return targetTxHash;
    }
    /**
     *
     * @param transaction
     * @param autoGasNative
     * @param sendTransactionData 发送交易时的元数据
     * @returns
     */
    sendTransactionWrapper = async (transaction, autoGasNative = false, sendTransactionData) => {
        // @ts-ignore
        const walletAddress = window.__webWalletData?.address;
        const chainName = this.chainName;
        const chainId = this.chainId;
        console.log('transaction', 'walletAddress', walletAddress);
        console.log('transaction', 'chainName', chainName);
        console.log('transaction', 'chainId', chainId);
        console.log('transaction', 'params', transaction, autoGasNative);
        await switchChain(this.provider, this.chainName, this.chainId);
        // @ts-ignore
        const transactionType = transaction?.[0]?.transactionType || transaction?.transactionType || '';
        if (this.enableAccountAbstraction()) {
            // @ts-ignore
            const smartAccount = (await this.smartAccount.getAccountConfig?.()) || {};
            const txs = Array.isArray(transaction) ? transaction : [transaction];
            const fns = [this.smartAccount.getFeeQuotes(transaction)];
            if (this?.accountAbstraction?.name === 'UNIVERSAL' && Number(this.chainId) !== PioneerChainId) {
                fns.push(this.createCrossChainUserOperation(smartAccount, txs));
            }
            const results = await Promise.all(fns);
            let feeQuotes = results[0];
            if (results?.length > 1) {
                let fee = new BigNumber(results?.[1]?.particleCost || '0x0').toString();
                const userOps = results?.[1]?.userOps || [];
                const PioneerChainPublicClient = window.__webWalletData.getPublicClient(PioneerChainId);
                const balance = await PioneerChainPublicClient.getBalance({
                    address: window.__webWalletData.address || '',
                }).then((res) => {
                    return res.toString();
                });
                // @ts-ignore
                feeQuotes.universalPaymaster = {
                    balance,
                    fee,
                    tokenInfo: chains.getEVMChainInfoById(PioneerChainId),
                    txs,
                    smartAccount,
                    smartAccountContractName: smartAccount.name,
                    transactionType,
                    usdgUserOps: userOps,
                };
            }
            return new Promise((resolve, reject) => {
                if (!autoGasNative) {
                    this.events.removeAllListeners('erc4337:sendTransaction');
                    this.events.removeAllListeners('erc4337:sendTransactionError');
                    this.events.once('erc4337:sendTransaction', async (data) => {
                        if (this.smartAccount) {
                            try {
                                const { feeQuote, userOpHash, userOp, tokenPaymasterAddress, smartAccount, smartAccountContractName, usdgUserOps, cfTurnstileResponse } = data || {};
                                if (smartAccountContractName === 'UNIVERSAL') {
                                    try {
                                        const hash = (await this.universalContractCall(smartAccount, usdgUserOps || [], cfTurnstileResponse));
                                        resolve(hash);
                                    }
                                    catch (error) {
                                        reject(error);
                                    }
                                }
                                else {
                                    const params = {
                                        tx: transaction,
                                        feeQuote,
                                        userOpHash,
                                        userOp,
                                        tokenPaymasterAddress,
                                    };
                                    Object.keys(params).forEach((key) => {
                                        // @ts-ignore
                                        if (params[key] === undefined) {
                                            // @ts-ignore
                                            delete params[key];
                                        }
                                    });
                                    const hash = await this.smartAccount.sendTransaction(params);
                                    try {
                                        const details = getTXUrl({
                                            chainType: 'evm',
                                            chainId: this.chainId,
                                            chainName: this.chainName,
                                            hash: hash || '',
                                        });
                                        console.log('transaction', 'hash', hash);
                                        console.log('transaction', {
                                            walletAddress,
                                            chainName,
                                            chainId,
                                            params: transaction,
                                            autoGasNative,
                                            hash,
                                            details,
                                        });
                                    }
                                    catch (error) {
                                        console.log('transaction', 'error', error);
                                    }
                                    if (!hash) {
                                        // 交易失败
                                        reject(new Error(this.t('transaction_failed')));
                                    }
                                    resolve(hash);
                                }
                            }
                            catch (error) {
                                reject(error);
                            }
                        }
                        else {
                            reject(new Error('ERC-4337 Service not available'));
                        }
                    });
                    this.events.once('erc4337:sendTransactionError', (error) => {
                        reject(error);
                    });
                    if (!feeQuotes.transactions) {
                        if (Array.isArray(transaction)) {
                            feeQuotes.transactions = transaction;
                        }
                        else {
                            feeQuotes.transactions = [transaction];
                        }
                    }
                    this.events.emit('erc4337:prepareTransaction', { ...feeQuotes, sendTransactionData });
                }
                else {
                    // use native gas
                    const verifyingPaymasterNative = feeQuotes.verifyingPaymasterNative || feeQuotes.verifyingPaymasterNative;
                    resolve(this.smartAccount.sendTransaction({
                        feeQuote: verifyingPaymasterNative?.feeQuote,
                        userOpHash: verifyingPaymasterNative?.userOpHash,
                    }));
                }
            });
        }
        else {
            return this.provider.request({
                method: 'eth_sendTransaction',
                params: [transaction],
            });
            // .then((res: any) => {
            //   if(res.message) {
            //     throw new Error(res.message);
            //   }
            //   return res?.result ?? res;
            // });
        }
    };
    getEvm1559support() {
        const chainInfo = getChainInfo();
        this.evm1559support =
            Object.entries(chainInfo)
                .map((item) => {
                return { ...item[1], chainName: item[0].toLowerCase() };
            })
                .find((item) => item.chainName == this.chainName.toLowerCase())?.evm1559support || false;
    }
    async getUserSimpleInfo(params) {
        return this.commonApi.getUserSimpleInfo(params);
    }
    async request(options = {}) {
        const { id, primaryKey, fn, dataBaseModel, useCache = true, isRequest = true, throttleWait = 0 } = options || {};
        let data;
        if (primaryKey) {
            data = await dataBaseModel.get(primaryKey).catch(database.errorHandler);
        }
        else {
            data = await dataBaseModel.get({ id }).catch(database.errorHandler);
        }
        if (isRequest === true && fn) {
            const isUseCache = useCache && data && data.updateTime && dayjs().diff(dayjs(data.updateTime), 'day') < 1;
            if (!isUseCache) {
                let res = await fn(data);
                if (Array.isArray(res)) {
                    res = { list: res };
                }
                else if (typeof res !== 'object') {
                    res = { value: res };
                }
                data = {
                    id,
                    ...(primaryKey || {}),
                    ...res,
                    updateTime: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
                };
                try {
                    await dataBaseModel.put(data);
                }
                catch (error) {
                    // ignore
                }
            }
            else {
                if (throttleWait === 0 || !data?.updateTime || (throttleWait > 0 && data?.updateTime && new Date().getTime() - new Date(data.updateTime).getTime() > throttleWait)) {
                    fn(data)
                        .then((res) => {
                        if (Array.isArray(res)) {
                            res = { list: res };
                        }
                        else if (typeof res !== 'object') {
                            res = { value: res };
                        }
                        try {
                            dataBaseModel.put({
                                id,
                                ...(primaryKey || {}),
                                ...res,
                                updateTime: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
                            });
                        }
                        catch (error) {
                            // ignore
                        }
                    })
                        .catch((err) => {
                        console.log(dataBaseModel.name, err);
                    });
                }
            }
        }
        return data;
    }
    setCurrency(currency) {
        this.currency = currency;
    }
    async getBalance() {
        let that = this;
        const account = await this.getAddress();
        const id = md5(`${this.chainName}_${this.chainId}_${account}`);
        let res = await this.request({
            id,
            useCache: false,
            fn: function () {
                return new Promise(async (resolve, reject) => {
                    try {
                        const targetChainId = await that.provider.request({ method: 'eth_chainId' });
                        const publicClient = window.__webWalletData.getPublicClient(Number(targetChainId));
                        return publicClient.request({ method: 'eth_getBalance', params: [account, 'latest'] }).then((val) => {
                            const value = ethjs.fromWei(new BN(stripHexPrefix(val), 16), 'wei');
                            resolve(value);
                        });
                    }
                    catch (error) {
                        reject(error);
                    }
                });
            },
            dataBaseModel: database.balanceInfo,
        });
        let balance = 0;
        if (res?.value) {
            balance = res.value;
        }
        const result = ethjs.fromWei(balance, this.evmUnit);
        console.log('getBalance', result);
        return result;
    }
    async getRequestTokenAndNftAddress(userAddress, chainName, chainId) {
        chainName = chainName || this.chainName;
        chainId = Number(chainId || this.chainId);
        let addressList = [];
        // token list start
        // 1: read storage tokenList
        const totkenList = getCacheTokenList(userAddress, chainName, chainId) || [];
        addressList = [...totkenList.map((item) => item.address), 'native'];
        // 2：importTokenList
        const chainInfo = chains.getEVMChainInfoById(chainId);
        const chainNetwork = chainInfo?.network?.toLowerCase?.();
        const impoertTokenList = await this.getImpoertTokenList({ userAddress, chainNetwork });
        addressList.push(...impoertTokenList.map((item) => item.tokenAddress));
        // @ts-ignore
        if (Number(window.__appParams.paramsChainId) === chainId) {
            // 3: displayTokenAddresses
            // @ts-ignore
            addressList.push(...(window.__appParams.displayTokenAddresses || []));
            // 4: priorityTokenAddresses
            // @ts-ignore
            addressList.push(...(window.__appParams.priorityTokenAddresses || []));
        }
        // 5:filter hiddenAddressList
        const hiddenAddressList = impoertTokenList.filter((item) => item.isHide).map((item) => item.address);
        addressList = addressList.filter((address) => address && address.length > 5 && !hiddenAddressList.includes(address));
        // token list end
        // nft list start
        // 1：get importNftList
        const importNftList = await this.getImportNftList({ walletAddress: userAddress, chainName, chainId });
        addressList.push(...importNftList.map((item) => item.nftAddress));
        // @ts-ignore
        if (Number(window.__appParams.paramsChainId) === chainId) {
            // 2：get displayNftList
            // @ts-ignore
            addressList.push(...(window.__appParams.displayNFTContractAddresses || []));
            // 3：get priorityNftList
            // @ts-ignore
            addressList.push(...(window.__appParams.priorityNFTContractAddresses || []));
        }
        // nft list end
        // filter repeat
        addressList = Array.from(new Set(addressList.filter((address) => !!address).map((item) => item.toLowerCase())));
        return addressList;
    }
    async getTokenAndNftCacheKey(chainName, chainId) {
        chainName = chainName || this.chainName;
        chainId = Number(chainId || this.chainId);
        const walletAccount = await this.getAddress();
        return md5(`${chainName}_${chainId}_${walletAccount}`.toLowerCase());
    }
    async getTokensAndNFTs(chainName, chainId) {
        chainName = chainName || this.chainName;
        chainId = Number(chainId || this.chainId);
        const walletAddress = await this.getAddress();
        let tokenData = await this.getAllToken(chainName, chainId);
        const importTokenList = await this.getImpoertTokenList({ userAddress: walletAddress?.toLowerCase(), chainName, chainId });
        const cacheKey = await this.getTokenAndNftCacheKey(chainName, chainId);
        let tokensAndNFTs = {};
        const tokenAddressList = await this.getRequestTokenAndNftAddress(walletAddress, chainName, chainId);
        tokensAndNFTs = await this.rpc?.getTokensAndNFTs(walletAddress, tokenAddressList, this.smartAccount, chainId).then((res) => {
            const data = res?.data?.result || {};
            // TODO
            // data.nfts = data.nfts.map((item: any) => {
            //   if (item.address === '0x551C75b06345ADa9f09014d65752a4F168a682B3') {
            //     item.isScam = true;
            //   }
            //   return item;
            // });
            return data;
        });
        let { nfts = [], tokens = [], native: nativeBalance } = tokensAndNFTs || {};
        const findData = Object.entries(getChainInfo())
            .map((item) => {
            item[0] = item[0].toLowerCase();
            return item;
        })
            .find((item) => item[0]?.toLowerCase?.() == chainName?.toLowerCase()) || [];
        let { icon: defaultIcon, nativeIcon, symbol: defaultSymbol } = findData[1] || {};
        const symbol = findData?.[1]?.chainData?.[this.chainId]?.nativeCurrency?.symbol;
        // tokens 前面添加 native
        tokens.unshift({
            address: 'native',
            amount: nativeBalance,
            image: nativeIcon || defaultIcon,
            name: symbol || defaultSymbol,
            symbol: symbol || defaultSymbol,
        });
        const addressList = [...new Set([...tokenAddressList, ...tokens.map((item) => item.address)])];
        const exchangeRateData = await this.getPrice(addressList, true, chainId, chainName);
        nfts = nfts.map((item) => {
            item.is1155 = !!item.isSemiFungible;
            if (item?.data?.animation_url) {
                item.animationUrl = item.data.animation_url;
            }
            else if (item?.animation_url) {
                item.animationUrl = item.animation_url;
            }
            return item;
        });
        // format data
        tokens.forEach((token) => {
            const price = (exchangeRateData.find((item) => item.address?.toLowerCase() == token.address?.toLowerCase()) || {})?.price || 0;
            if (!token.image && token.address !== 'native') {
                token.image = tokenData.tokens.find((item) => item.address?.toLowerCase() == token.address?.toLowerCase())?.logoURI || '';
            }
            token.image = token.image || defaultTokenIcon;
            if (token.address === 'native') {
                token.decimals = getParticleChainInfo(chainName || '', Number(chainId))?.decimals ?? 18;
            }
            if (!token.decimals) {
                token.decimals = 0;
            }
            // @ts-ignore
            token.symbol = token.symbol?.replace?.(/\00+/g, '');
            if (token.symbol && token.symbol.length > 24) {
                token.symbol = token.symbol.substring(0, 24) + '...';
            }
            if (!token.decimals) {
                token.decimals = 0;
            }
            // @ts-ignore
            token.symbol = token.symbol?.replace?.(/\00+/g, '');
            if (token.symbol && token.symbol.length > 24) {
                token.symbol = token.symbol.substring(0, 24) + '...';
            }
            const decimals = Number(token.decimals ?? 18);
            // @ts-ignore
            token.amount = new BigNumber(token.amount || 0).dividedBy(new BigNumber(10 ** decimals)).toFixed(decimals || 14);
            token.amount = shakeZero(token.amount);
            token.unitPrice = price;
            token.price = Number(new BigNumber(token.amount || 0).multipliedBy(price));
            token['24hChange'] = (exchangeRateData.find((item) => item.address?.toLowerCase() == token.address?.toLowerCase()) || {})['24hChange'] || '';
            if (token['24hChange']) {
                token['24hChange'] = Number(toNonExponential(token['24hChange'], 2));
            }
            else {
                token['24hChange'] = 0;
            }
        });
        let totalPrice = tokens.reduce((total, item) => {
            return item.price + total;
        }, 0) || 0;
        await database.tokensAndNFTs.put({
            id: cacheKey,
            ...tokensAndNFTs,
            tokens,
            nfts,
            totalPrice,
            walletAddress,
            exchangeRateData,
            importTokenList,
            updateTime: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
        });
        return {
            chainName,
            chainId,
            allTokens: tokenData,
            nfts,
            tokens,
            totalPrice,
            walletAddress,
            exchangeRateData,
            importTokenList,
        };
    }
    // address
    async getAddress() {
        try {
            if (this.mockAddress) {
                return this.mockAddress;
                // @ts-ignore
            }
            else if (window.logoutFlag === true) {
                return '';
            }
            else if (this.enableAccountAbstraction()) {
                const provider = !window.__webWalletData?.inAuthCoreIframe ? this.provider : this.smartAccount?.provider;
                if (provider) {
                    const accountAbstractionChains = this.accountAbstractionChains();
                    const providerChainId = Number(await provider?.request({ method: 'eth_chainId' }));
                    if (!accountAbstractionChains?.includes(providerChainId)) {
                        // await switchChain(provider, this.chainName, this.chainId);
                        await window.__webWalletData.walletSwitchChain(provider, this.chainId);
                    }
                }
                return await this.smartAccount.getAddress();
            }
            else {
                return await this.provider.request({ method: 'eth_accounts' }).then((res) => {
                    return res[0] ?? '';
                });
            }
        }
        catch (error) {
            console.log('getAddress', error);
            return '';
        }
    }
    async getPrice(addressList, useCache = false, chainId, chainName) {
        chainName = chainName || this.chainName;
        chainId = Number(chainId || this.chainId);
        let that = this;
        const account = await this.getAddress();
        addressList = addressList
            .filter((address) => !!address)
            .map((address) => {
            if (address?.toLowerCase() == EVM_SWAP_NATIVE_ADDDRESS?.toLowerCase?.()) {
                return 'native';
            }
            else {
                return address;
            }
        });
        const id = md5(`${chainName}_${chainId}_${account}_${addressList.join('_')}_${this.currency}`);
        let res = await this.request({
            id,
            throttleWait: 1000 * 10,
            useCache,
            fn: () => {
                return this.rpc.getPrice(addressList, [this.currency], Number(chainId)).then((res) => {
                    return res.data.result;
                });
            },
            dataBaseModel: database.exchangeRate,
        });
        let { list } = res;
        list = (list || []).map((item) => {
            const currencie = item.currencies.find((o) => o.type.toLowerCase() == this.currency.toLowerCase()) || {};
            return { address: item.address, price: currencie.price, ['24hChange']: currencie['24hChange'], currency: that.currency.toLowerCase() };
        });
        return list;
    }
    async getWbe3() {
        if (!this.web3) {
            const { default: Web3 } = await import('web3');
            this.web3 = new Web3(this.provider);
        }
        return this.web3;
    }
    /**
     * isPersonalAddress
     * @param address
     */
    async isPersonalAddress(address) {
        try {
            if (this.chainName.toLowerCase() == 'tron') {
                return true;
            }
            await this.getWbe3();
            address = toChecksumAddress(address);
            const code = await this.web3.eth.getCode(address);
            return code === '0x';
        }
        catch (error) {
            console.log('isPersonalAddress', error);
            return false;
        }
    }
    // getAllToken
    async getAllToken(chainName, chainId) {
        chainName = (chainName || this.chainName || '');
        chainId = Number(chainId || this.chainId);
        const id = md5(`${chainName}_${chainId}`);
        let data = await this.request({
            id,
            primaryKey: {
                chainType: this.chainType,
                chainId,
                chainName,
            },
            throttleWait: 1000 * 60 * 5,
            fn: () => {
                return this.commonApi.getAllToken(chainName?.toLowerCase?.() || '');
            },
            dataBaseModel: database.allTokenInfo,
        });
        return data;
    }
    async getNftNetworkFees(nftAddress, tokenId, toAddress, amount = 1) {
        let result = {};
        // if (!ValidEvmAddress.test(toAddress) || !(await this.isPersonalAddress(toAddress))) {
        // if (!ValidEvmAddress.test(toAddress) || !isValidAddress(toAddress) || || !isValidChecksumAddress(toChecksumAddress(toAddress))) {
        if (!ValidEvmAddress.test(toAddress) || !isValidAddress(toAddress)) {
            throw new Error(this.t('invalid_address'));
        }
        if (!toAddress) {
            return result;
        }
        const account = await this.getAddress();
        const txParams = await this.getNftTransactionParams(nftAddress, toAddress, tokenId, amount);
        const suggestedGasFees = await this.getSuggestedGasFees();
        let gasLimit = '0x0';
        if (this.chainName.toLowerCase() !== 'tron') {
            gasLimit = await this.getEstimateGas({
                from: account,
                to: nftAddress,
                value: '0x0',
                data: txParams?.data,
            });
        }
        let prices = await this.getPrice(['native'], true);
        prices = [
            {
                address: 'native',
                currencies: prices.map((item) => {
                    return {
                        ...item,
                        type: this.currency.toLowerCase(),
                        price: item.price,
                        marketCap: 0,
                        '24hChange': 0,
                        '24hVol': 0,
                        lastUpdatedAt: '',
                    };
                }),
            },
        ];
        result = gasNetworkFee({
            evm1559support: this.evm1559support,
            gasFee: suggestedGasFees,
            gasLimit,
            prices,
        });
        return result;
    }
    async abiEncodeFunctionCall(contractAddress, functionName, params) {
        let dataBuffer;
        if (functionName === TransactionSmartType.ERC20_TRANSFER) {
            dataBuffer = simpleEncode('transfer(address,uint256)', ...params);
        }
        else if (functionName === TransactionSmartType.ERC721_TRANFER) {
            dataBuffer = simpleEncode('safeTransferFrom(address,address,uint256)', ...params);
        }
        else if (functionName === TransactionSmartType.ERC1155_TRANFER) {
            dataBuffer = simpleEncode('safeTransferFrom(address,address,uint256,uint256,bytes)', ...params);
        }
        return addHexPrefix(dataBuffer?.toString?.('hex') || '');
    }
    async getNetworkFees(from, to, amount) {
        let gasLimit = '0x0';
        if (!from || !to) {
            return 0;
        }
        const account = await this.getAddress();
        if (this.chainName.toLowerCase() == 'tron') {
            gasLimit = '0x0';
        }
        else if (from === 'native') {
            // const value = `0x${toWei(amount, this.evmUnit).toString(16)}`;
            const params = {
                value: '0x0',
                to,
                data: '0x',
                from: account,
            };
            gasLimit = await this.getEstimateGas(params);
        }
        else {
            // const decimals = await this.getDecimalsByAddress(from);
            let result = await this.abiEncodeFunctionCall(from, TransactionSmartType.ERC20_TRANSFER, [to, '0']);
            gasLimit = await this.getEstimateGas({
                from: account,
                to: from,
                value: '0x0',
                data: result,
            });
        }
        const suggestedGasFees = await this.getSuggestedGasFees();
        const { baseFee = '', high = {} } = suggestedGasFees;
        const { maxFeePerGas, maxPriorityFeePerGas } = high;
        const { maxFee = 0 } = gasFee({
            baseFee,
            maxFeePerGas,
            maxPriorityFeePerGas,
            gasLimit,
        });
        console.log('networkFees', maxFee);
        return { value: Number(maxFee) * 1.01 };
    }
    async nativeTransactionParams(from, to, amount) {
        const balance = new BigNumber(await this.getBalance());
        if (balance.lt(new BigNumber(amount))) {
            throw new Error(this.t('insufficient_balance'));
        }
        const suggestedGasFees = await this.getSuggestedGasFees();
        const maxFeePerGas = `0x${toWei(suggestedGasFees.high.maxFeePerGas, 'gwei').toString(16)}`;
        const maxPriorityFeePerGas = `0x${toWei(suggestedGasFees.high.maxPriorityFeePerGas, 'gwei').toString(16)}`;
        const chain = chains.getEVMChainInfoById(Number(this.chainId));
        const decimals = chain?.nativeCurrency?.decimals || 18;
        amount = '0x' + new BigNumber(amount).multipliedBy(new BigNumber(10).pow(decimals)).integerValue(1).toString(16);
        let gasLimit = '0x0';
        if (this.chainName.toLowerCase() !== 'tron') {
            gasLimit = await this.getEstimateGas({ value: amount, to, data: '0x', from });
        }
        let result = {
            from,
            to,
            value: amount,
            type: this.transactionChainType,
            action: 'normal',
            data: '0x',
            gasLevel: 'high',
            // gasLimit,
        };
        if (this.transactionChainType === '0x0') {
            result.gasPrice = maxFeePerGas;
            // result.gas = maxPriorityFeePerGas;
        }
        else {
            result.maxFeePerGas = maxFeePerGas;
            result.maxPriorityFeePerGas = maxPriorityFeePerGas;
        }
        return result;
    }
    async erc20TransactionParams(from, to, amount) {
        const account = await this.getAddress();
        const decimals = await this.getDecimalsByAddress(from);
        const amountValue = new BigNumber(amount).multipliedBy(new BigNumber(10).pow(decimals)).integerValue(1).toString(10);
        const data = await this.abiEncodeFunctionCall(from, TransactionSmartType.ERC20_TRANSFER, [to, amountValue]);
        await this.getTransactionChainType();
        const txnParams = {
            from: account,
            to: from,
            value: '0x0',
            data,
            gasLevel: 'high',
            type: this.transactionChainType,
        };
        return txnParams;
    }
    async erc1155TransactionParams(from, to = '', tokenId, nftAddress, amount = 1) {
        const result = await this.abiEncodeFunctionCall(nftAddress, TransactionSmartType.ERC1155_TRANFER, [from, to, tokenId, amount.toString(), Buffer.from('', 'utf8')]);
        const transactionChainType = await this.getTransactionChainType();
        const txnParams = {
            from,
            to: nftAddress,
            value: '0x0',
            data: result,
            action: 'normal',
            gasLevel: 'high',
            type: transactionChainType,
        };
        return txnParams;
    }
    async erc721TransactionParams(from, to = '', tokenId, nftAddress) {
        const result = await this.abiEncodeFunctionCall(nftAddress, TransactionSmartType.ERC721_TRANFER, [from, to, tokenId]);
        const transactionChainType = await this.getTransactionChainType();
        const txnParams = {
            from,
            to: nftAddress,
            value: '0x0',
            data: result,
            action: 'normal',
            gasLevel: 'high',
            type: transactionChainType,
        };
        return txnParams;
    }
    async getNftTransactionParams(nftAddress, toAddress, tokenId, amount = 1) {
        const account = await this.getAddress();
        const id = await this.getTokenAndNftCacheKey();
        const data = await database.tokensAndNFTs.get({ id }).catch(database.errorHandler);
        const isErc1155 = data?.nfts?.find?.((item) => item.address?.toLowerCase?.() == nftAddress?.toLowerCase?.() && item.tokenId == tokenId)?.is1155 || false;
        console.log('isErc1155', nftAddress, tokenId, isErc1155);
        let txnParams;
        if (isErc1155) {
            txnParams = await this.erc1155TransactionParams(account, toAddress, tokenId, nftAddress || '', amount);
        }
        else {
            txnParams = await this.erc721TransactionParams(account, toAddress, tokenId, nftAddress || '');
        }
        return txnParams;
    }
    async nftBatchSend(to, nfts) {
        let toAddress = to;
        if (window.__webWalletData?.isBtcConnect && validBitcoinAddress(toAddress)) {
            // btc to evm
            toAddress = await this.getEvmAddressByBtcAddress(toAddress);
        }
        else if (isValidDID(to) && isChainSupportDID({ id: Number(this.chainId), name: this.chainName })) {
            toAddress = await this.getAddressFromDID(to);
        }
        if (!ValidEvmAddress.test(toAddress) || !isValidAddress(toAddress)) {
            throw new Error(this.t('invalid_address'));
        }
        const account = await this.getAddress();
        if (account?.toLowerCase() === toAddress?.toLowerCase()) {
            throw new Error(this.t('you_can_t_send_to_yourself'));
        }
        // switchChain
        await switchChain(this.provider, this.chainName, this.chainId);
        const suggestedGasFees = await this.getSuggestedGasFees();
        const maxFeePerGas = `0x${toWei(suggestedGasFees.medium.maxFeePerGas, 'gwei').toString(16)}`;
        const maxPriorityFeePerGas = `0x${toWei(suggestedGasFees.medium.maxPriorityFeePerGas, 'gwei').toString(16)}`;
        for (let i = 0; i < nfts.length; i++) {
            const { address, tokenId, amount } = nfts[i];
            let txnParams = await this.getNftTransactionParams(address, toAddress, tokenId, amount);
            txnParams.maxFeePerGas = maxFeePerGas;
            txnParams.maxPriorityFeePerGas = maxPriorityFeePerGas;
            if (txnParams.type === '0x0') {
                txnParams.gasPrice = txnParams?.maxFeePerGas || '';
                txnParams.gas = txnParams?.gasLimit || '';
                delete txnParams.maxFeePerGas;
                delete txnParams.maxPriorityFeePerGas;
            }
            if (this.chainName.toLowerCase() == 'tron') {
                delete txnParams.gasPrice;
                delete txnParams.gas;
            }
            nfts[i].txnParams = txnParams;
        }
        return this.sendTransactionWrapper(nfts.map((item) => {
            return { ...item.txnParams, transactionType: 'nft' };
        }));
    }
    async sendNftTransaction(nftAddress, to, tokenId, amount = 1) {
        let toAddress = to;
        if (window.__webWalletData?.isBtcConnect && validBitcoinAddress(toAddress)) {
            // btc to evm
            toAddress = await this.getEvmAddressByBtcAddress(toAddress);
        }
        else if (isValidDID(to) && isChainSupportDID({ id: Number(this.chainId), name: this.chainName })) {
            toAddress = await this.getAddressFromDID(to);
        }
        if (!ValidEvmAddress.test(toAddress) || !isValidAddress(toAddress)) {
            throw new Error(this.t('invalid_address'));
        }
        const account = await this.getAddress();
        if (account?.toLowerCase() === toAddress?.toLowerCase()) {
            throw new Error(this.t('you_can_t_send_to_yourself'));
        }
        // switchChain
        await switchChain(this.provider, this.chainName, this.chainId);
        let txnParams = await this.getNftTransactionParams(nftAddress, toAddress, tokenId, amount);
        // const gasLimit = await this.getEstimateGas({ value: `0x0`, to: nftAddress, data: txnParams.data, from: account });
        // txnParams.gasLimit = gasLimit;
        const suggestedGasFees = await this.getSuggestedGasFees();
        const maxFeePerGas = `0x${toWei(suggestedGasFees.medium.maxFeePerGas, 'gwei').toString(16)}`;
        const maxPriorityFeePerGas = `0x${toWei(suggestedGasFees.medium.maxPriorityFeePerGas, 'gwei').toString(16)}`;
        txnParams.maxFeePerGas = maxFeePerGas;
        txnParams.maxPriorityFeePerGas = maxPriorityFeePerGas;
        if (txnParams.type === '0x0') {
            txnParams.gasPrice = txnParams?.maxFeePerGas || '';
            txnParams.gas = txnParams?.gasLimit || '';
            delete txnParams.maxFeePerGas;
            delete txnParams.maxPriorityFeePerGas;
        }
        if (this.chainName.toLowerCase() == 'tron') {
            delete txnParams.gasPrice;
            delete txnParams.gas;
        }
        return this.sendTransactionWrapper({ ...txnParams, transactionType: 'nft' });
    }
    async processTokens(tokens, account, to) {
        const promises = tokens.map(async (token) => {
            const { address: from, amount } = token;
            let txnParams = {};
            if (from === 'native') {
                txnParams = await this.nativeTransactionParams(account, to, amount);
            }
            else {
                txnParams = await this.erc20TransactionParams(from, to, amount);
            }
            if (txnParams.type === '0x0') {
                txnParams.gasPrice = txnParams?.maxFeePerGas || '';
                txnParams.gas = txnParams?.gasLimit || '';
                delete txnParams.maxFeePerGas;
                delete txnParams.maxPriorityFeePerGas;
            }
            if (this.chainName.toLowerCase() === 'tron') {
                delete txnParams.gasPrice;
                delete txnParams.gas;
            }
            token.txnParams = txnParams;
            return token;
        });
        return Promise.all(promises);
    }
    async tokenBatchSend(to, tokens) {
        const account = await this.getAddress();
        let toAddress = to;
        if (window.__webWalletData?.isBtcConnect && validBitcoinAddress(toAddress)) {
            // btc to evm
            toAddress = await this.getEvmAddressByBtcAddress(toAddress);
        }
        else if (isValidDID(to) && isChainSupportDID({ id: Number(this.chainId), name: this.chainName })) {
            toAddress = await this.getAddressFromDID(to);
        }
        if (!ValidEvmAddress.test(toAddress)) {
            throw new Error(this.t('invalid_address'));
        }
        if (account?.toLowerCase() === toAddress?.toLowerCase()) {
            throw new Error(this.t('you_can_t_send_to_yourself'));
        }
        await switchChain(this.provider, this.chainName, this.chainId);
        this.transactionChainType = await this.getTransactionChainType();
        tokens = await this.processTokens(tokens, account, toAddress);
        const sendTransactionData = tokens.map((item) => {
            return {
                amount: item.amount,
                decimals: item.decimals || this.getDecimalsByAddress(item.address),
                from: item.address,
                type: TransactionType.BATCH_TRANSFER,
            };
        });
        return this.sendTransactionWrapper(tokens.map((item) => item.txnParams), false, sendTransactionData);
    }
    async sendTransaction(from, to = '', amount = '0', type) {
        const account = await this.getAddress();
        let toAddress = to;
        if (window.__webWalletData?.isBtcConnect && validBitcoinAddress(toAddress)) {
            // btc to evm
            toAddress = await this.getEvmAddressByBtcAddress(toAddress);
        }
        else if (isValidDID(to) && isChainSupportDID({ id: Number(this.chainId), name: this.chainName })) {
            toAddress = await this.getAddressFromDID(to);
        }
        // if (type !== TransactionType.UPGRADE_ACCOUNT && account?.toLowerCase() === toAddress?.toLowerCase()) {
        //   throw new Error(this.t('you_can_t_send_to_yourself'));
        // }
        if (!ValidEvmAddress.test(toAddress)) {
            throw new Error(this.t('invalid_address'));
        }
        if (type !== TransactionType.UPGRADE_ACCOUNT && (Number(amount) || 0) <= 0) {
            throw new Error(this.t('invalid_amount'));
        }
        // switchChain
        await switchChain(this.provider, this.chainName, this.chainId);
        this.transactionChainType = await this.getTransactionChainType();
        let txnParams = {};
        if (from === 'native') {
            txnParams = await this.nativeTransactionParams(account, toAddress, amount);
        }
        else {
            txnParams = await this.erc20TransactionParams(from, toAddress, amount);
        }
        // 元发送交易数据
        const sendTransactionData = [
            {
                from,
                to,
                amount,
                txnParams,
            },
        ];
        return this.sendTransactionWrapper(txnParams, false, sendTransactionData);
    }
    async getTransactionChainType(chainId) {
        const chainInfo = chains.getEVMChainInfoById(Number(chainId || this.chainId || 1));
        const features = chainInfo?.features || [];
        return features.find((item) => item.name === 'EIP1559') ? '0x2' : '0x0';
    }
    async getSpeedupAndCancelMaxFeePerGasParams(oldMaxFeePerGas, oldMaxPriorityFeePerGas, estimateGasParams, oldGasPrice) {
        const gasLevel = 'custom';
        const suggestedGasFees = await this.getSuggestedGasFees();
        const lowMaxFeePerGasBN = toWei(suggestedGasFees.low.maxFeePerGas, 'gwei');
        const lowMaxPriorityFeePerGasBN = toWei(suggestedGasFees.low.maxPriorityFeePerGas, 'gwei');
        let gasLimit = '0x0';
        if (this.chainName.toLowerCase() !== 'tron') {
            gasLimit = await this.getEstimateGas(estimateGasParams);
        }
        let result = {
            gasLimit,
            gasLevel,
        };
        if (oldGasPrice) {
            const gasPriceBN = new BN(stripHexPrefix(oldGasPrice), 16).mul(new BN(13)).div(new BN(10));
            result.gasPrice = `0x${gasPriceBN.toString(16)}`;
            result.type = '0x0';
        }
        else {
            oldMaxFeePerGas = oldMaxFeePerGas ? ethjs.fromWei(oldMaxFeePerGas, 'gwei') : suggestedGasFees.high.maxFeePerGas;
            oldMaxPriorityFeePerGas = oldMaxPriorityFeePerGas ? ethjs.fromWei(oldMaxPriorityFeePerGas, 'gwei') : suggestedGasFees.high.maxPriorityFeePerGas;
            // oldMaxFeePerGas*13/10 == oldMaxFeePerGas*1.3 bn safe
            let maxFeePerGasBN = toWei(oldMaxFeePerGas, 'gwei').mul(new BN(13)).div(new BN(10));
            if (maxFeePerGasBN.lt(lowMaxFeePerGasBN)) {
                maxFeePerGasBN = lowMaxFeePerGasBN;
            }
            // oldMaxPriorityFeePerGas*11/10 == oldMaxPriorityFeePerGas*1.1  bn safe
            let maxPriorityFeePerGasBN = toWei(oldMaxPriorityFeePerGas, 'gwei').mul(new BN(11)).div(new BN(10));
            if (maxPriorityFeePerGasBN.lt(lowMaxPriorityFeePerGasBN)) {
                maxPriorityFeePerGasBN = lowMaxPriorityFeePerGasBN;
            }
            result.maxFeePerGas = `0x${maxFeePerGasBN.toString(16)}`;
            result.maxPriorityFeePerGas = `0x${maxPriorityFeePerGasBN.toString(16)}`;
            result.type = '0x2';
        }
        return result;
    }
    async cancelTransaction(params) {
        let { data = '0x', from, nonce, to, value = '', gasPrice: oldGasPrice } = params;
        if (!nonce) {
            throw new Error(this.t('transaction_has_been_confirmed'));
        }
        if (!value) {
            throw new Error('Value is required');
        }
        // switchChain
        await switchChain(this.provider, this.chainName, this.chainId);
        const account = await this.getAddress();
        // const estimateGasParams = { value: `0x${new BN(value).toString(16)}`, to: account, data: '0x', from };
        const estimateGasParams = { value, to: account, data: '0x', from };
        const { maxFeePerGas, maxPriorityFeePerGas, gasLevel, gasPrice, gasLimit, type } = await this.getSpeedupAndCancelMaxFeePerGasParams(params.maxFeePerGas, params.maxPriorityFeePerGas, estimateGasParams, oldGasPrice);
        const txnParams = {
            action: 'cancel',
            data: '0x',
            from: account.toLowerCase(),
            gasLevel,
            gasLimit,
            maxFeePerGas,
            maxPriorityFeePerGas,
            gasPrice,
            nonce,
            to: account.toLowerCase(),
            type,
            value: '0x0',
        };
        console.log('txnParams', txnParams);
        return this.sendTransactionWrapper(txnParams);
    }
    async speedupTransaction(params) {
        let { data = '0x', from, hash, nonce, to, value = '', gasPrice: oldGasPrice } = params;
        if (!nonce) {
            throw new Error(this.t('transaction_has_been_confirmed'));
        }
        if (!value) {
            throw new Error('Value is required');
        }
        // switchChain
        await switchChain(this.provider, this.chainName, this.chainId);
        // value = ethjs.toWei(value.toString(), this.evmUnit).toString();
        // value = `0x${new BN(value).toString(16)}`;
        const estimateGasParams = { value, to, data, from };
        const { maxFeePerGas, maxPriorityFeePerGas, gasLevel, gasPrice, gasLimit, type } = await this.getSpeedupAndCancelMaxFeePerGasParams(params.maxFeePerGas, params.maxPriorityFeePerGas, estimateGasParams, oldGasPrice);
        const txnParams = {
            data,
            from,
            gasLimit,
            gasLevel,
            maxFeePerGas,
            maxPriorityFeePerGas,
            gasPrice,
            nonce: Number(nonce),
            to,
            type,
            value,
            action: 'speedup',
        };
        console.log('txnParams', txnParams);
        return this.sendTransactionWrapper(txnParams);
    }
    getTransactionsTypeName = (itemData) => {
        const t = this.t;
        switch (itemData.deserializedData?.type) {
            case TransactionsType.RSS3_NATIVE_SEND:
                return t('send_n').format(itemData.amountChangeNatives?.[0]?.symbol) || '';
            case TransactionsType.RSS3_NATIVE_RECEIVE:
                return t('receive_n').format(itemData.amountChangeNatives?.[0]?.symbol) || '';
            case TransactionsType.NATIVE_SEND:
                return t('send_n').format(itemData.amountChangeNatives?.[0]?.symbol) || '';
            case TransactionsType.NATIVE_RECEIVE:
                return t('receive_n').format(itemData.amountChangeNatives?.[0]?.symbol) || '';
            case TransactionsType.RSS3_TOKEN_SEND:
                return t('send_n').format(itemData.amountChangeTokens?.[0]?.symbol) || '';
            case TransactionsType.RSS3_TOKEN_RECEIVE:
                return t('receive_n').format(itemData.amountChangeTokens?.[0]?.symbol) || '';
            case TransactionsType.RSS3_ERC_20_APPROVE:
                return t('approve_erc_n').format(itemData.amountChangeTokens?.[0]?.symbol || '');
            case TransactionsType.RSS3_ERC_20_BURN:
                return t('burn_n').format(itemData.amountChangeTokens?.[0]?.symbol || '');
            case TransactionsType.RSS3_NFT_SEND:
                return t('send_nft');
            case TransactionsType.RSS3_NFT_RECEIVE:
                return t('receive_nft');
            case TransactionsType.RSS3_NFT_APPROVE:
                return t('approve_n').format(itemData.amountChangeNfts?.[0]?.name || '');
            case TransactionsType.RSS3_NFT_MINT:
                return t('mint_nft');
            case TransactionsType.RSS3_NFT_BUY:
                return t('buy_nft');
            case TransactionsType.RSS3_SWAP:
                return t('swap');
            case TransactionsType.CHECK_IN:
                return t('check_in');
            case TransactionsType.UNKNOWN:
                return itemData.functionName || t('contract_interaction');
            default:
                return itemData.functionName || t('contract_interaction');
        }
    };
    async formatChangeAmountList(item, account) {
        // @ts-ignore
        const { decimals: nativeDecimals, symbol: nativeSymbol } = window.__webWalletData.chainInfo || {};
        const { fees } = item;
        const amountChangeList = [];
        let totalBN = new BigNumber('0');
        if (fees) {
            const networkFeesBN = new BigNumber(fees?.replace(/^-/, '')).dividedBy(new BigNumber(10).pow(nativeDecimals));
            item.networkFees = networkFeesBN.toString();
            item.networkFeesView = tokenAmountFormat(networkFeesBN);
            totalBN = totalBN.plus(networkFeesBN);
        }
        item.networkFeesSymbol = nativeSymbol;
        const natives = [];
        const estimatedChangesNatives = item?.deserializedData?.estimatedChanges?.natives || [];
        for (let i = 0; i < estimatedChangesNatives.length; i++) {
            const o = estimatedChangesNatives[i];
            let amountView = o?.amountChange;
            if (amountView) {
                const amountBN = new BigNumber(amountView).dividedBy(new BigNumber(10).pow(nativeDecimals));
                amountView = tokenAmountFormat(amountBN);
                // totalBN + 绝对值(amountBN)
                totalBN = totalBN.plus(amountBN.absoluteValue());
            }
            // @ts-ignore
            const { symbol, icon } = window.__webWalletData.chainInfo || {};
            const nativeIcon = getNativeIcon(chains.getEVMChainInfoById(Number(this.chainId)));
            natives.push({
                ...o,
                amountView,
                symbol,
                icon: nativeIcon,
                itemType: 'native',
            });
        }
        const tokens = [];
        const estimatedChangesTokens = item?.deserializedData?.estimatedChanges?.tokens || [];
        for (let i = 0; i < estimatedChangesTokens.length; i++) {
            const token = estimatedChangesTokens[i];
            const { amountChange, decimals } = token;
            let amountView = '';
            if (amountChange) {
                amountView = tokenAmountFormat(new BigNumber(amountChange || '').div(new BigNumber(10).pow(new BigNumber(decimals || '0'))));
            }
            const type = Number(amountView) > 0 ? 'in' : 'out';
            const icon = token.image || (await this.getTokenIconByAddress(token.address || ''));
            tokens.push({
                ...token,
                amountView,
                type,
                icon,
                itemType: 'token',
            });
        }
        const nfts = [];
        const estimatedChangesNfts = item?.deserializedData?.estimatedChanges?.nfts || [];
        for (let i = 0; i < estimatedChangesNfts.length; i++) {
            const nft = estimatedChangesNfts[i];
            const { amountChange, symbol, name, image, standard } = nft;
            const amountView = amountChange;
            const type = Number(amountView) > 0 ? 'in' : 'out';
            nfts.push({
                ...nft,
                amountView,
                type,
                icon: image || '',
                symbol,
                standard,
                name,
                itemType: 'nft',
            });
        }
        [natives, tokens, nfts].forEach((list) => {
            // 修正 加减
            list.forEach((o) => {
                let { amountView = '', toAddress = '', fromAddress = '' } = o;
                amountView = amountView.replace(/^[\+\-]/, '');
                /**
                 * is send to self
                 */
                const isSendToSelf = fromAddress?.toLowerCase() === toAddress?.toLowerCase();
                if (!isSendToSelf && account.toLowerCase() === toAddress?.toLowerCase()) {
                    amountView = '+' + amountView;
                }
                else if (!isSendToSelf && account.toLowerCase() === fromAddress?.toLowerCase()) {
                    amountView = '-' + amountView;
                }
                o.amountView = amountView;
            });
        });
        if (item.deserializedType === TransactionsType.RSS3_NFT_APPROVE || item.deserializedType === TransactionsType.RSS3_ERC_20_APPROVE) {
            // Approve 没有加减之分
            [natives, tokens, nfts].forEach((list) => {
                list?.forEach((o) => {
                    let amountView = o.amountView || '';
                    if (amountView) {
                        o.amountView = amountView?.replace(/^[\+\-]/, '');
                    }
                });
            });
        }
        // if (
        //   item.deserializedType === TransactionsType.RSS3_NFT_APPROVE ||
        //   item.deserializedType === TransactionsType.RSS3_NFT_SEND ||
        //   item.deserializedType === TransactionsType.RSS3_NFT_RECEIVE ||
        //   item.deserializedType === TransactionsType.RSS3_NFT_BUY ||
        //   item.deserializedType === TransactionsType.RSS3_NFT_MINT
        // ) {
        //   // nft amount 没有加减之分
        //   // nfts?.forEach((o: any) => {
        //   //   let amountView = o.amountView || '';
        //   //   if (amountView) {
        //   //     o.amountView = amountView?.replace(/^[\+\-]/, '');
        //   //   }
        //   // });
        // }
        if (item.deserializedType === TransactionsType.NATIVE_SEND ||
            item.deserializedType === TransactionsType.RSS3_NATIVE_SEND ||
            item.deserializedType === TransactionsType.NATIVE_RECEIVE ||
            item.deserializedType === TransactionsType.RSS3_NATIVE_RECEIVE) {
            amountChangeList.push(...natives);
        }
        else if (item.deserializedType === TransactionsType.RSS3_ERC_20_BURN ||
            item.deserializedType === TransactionsType.RSS3_ERC_20_APPROVE ||
            item.deserializedType === TransactionsType.RSS3_TOKEN_RECEIVE ||
            item.deserializedType === TransactionsType.RSS3_SWAP ||
            item.deserializedType === TransactionsType.TOKEN_SEND ||
            item.deserializedType === TransactionsType.RSS3_TOKEN_SEND) {
            // token
            amountChangeList.push(...tokens);
        }
        else if (item.deserializedType === TransactionsType.RSS3_NFT_APPROVE ||
            item.deserializedType === TransactionsType.RSS3_NFT_SEND ||
            item.deserializedType === TransactionsType.RSS3_NFT_RECEIVE ||
            item.deserializedType === TransactionsType.RSS3_NFT_BUY ||
            item.deserializedType === TransactionsType.RSS3_NFT_MINT) {
            amountChangeList.push(...nfts);
        }
        else {
            // 变化的部分，有Native变化优先展示Native，没有再看token有没有要展示的，右下角只显示一条变化
            amountChangeList.push(...natives);
            amountChangeList.push(...tokens);
        }
        // else if (item.deserializedType === TransactionsType.OTHER && item?.deserializedData?.estimatedChanges?.natives?.length) {
        //   const nativeData = item?.deserializedData?.estimatedChanges?.natives?.[0];
        //   const nativeDecimals = await this.getDecimalsByAddress('0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee');
        //   let amountView = tokenAmountFormat(new BigNumber(nativeData?.amountChange || '').div(new BigNumber(10).pow(new BigNumber(nativeDecimals || '0'))));
        //   if (nativeData?.fromAddress?.toLowerCase() === account.toLowerCase()) {
        //     amountView = `-${amountView}`;
        //   }
        //   amountChangeList.push({
        //     ...nativeData,
        //     amountView,
        //     symbol: nativeSymbol,
        //   });
        // }
        return {
            amountChangeList,
            totalBN,
            amountChangeNatives: natives,
            amountChangeTokens: tokens,
            amountChangeNfts: nfts,
        };
    }
    getTransactionsActionName(item, walletAddress) {
        let actionName = undefined;
        if (item.deserializedType === TransactionsType.RSS3_TOKEN_RECEIVE ||
            item.deserializedType === TransactionsType.RSS3_NFT_RECEIVE ||
            item.deserializedType === TransactionsType.RSS3_NATIVE_RECEIVE ||
            (item.to?.toLowerCase() === walletAddress?.toLowerCase() && item.from?.toLowerCase() !== walletAddress?.toLowerCase())) {
            actionName = 'in';
        }
        else if (item.from?.toLowerCase() === walletAddress?.toLowerCase()) {
            actionName = 'out';
        }
        else {
            actionName = item.from?.toLowerCase?.() === walletAddress?.toLowerCase?.() ? 'out' : 'in';
        }
        if (item.deserializedType === TransactionsType.RSS3_NATIVE_RECEIVE) {
            // receive native
            item.from = item?.deserializedData?.estimatedChanges?.natives?.[0]?.fromAddress || item.from;
        }
        else if (item.deserializedType === TransactionsType.RSS3_TOKEN_RECEIVE) {
            // receive token
            item.from = item?.deserializedData?.estimatedChanges?.tokens?.[0]?.fromAddress || item.from;
        }
        else if (item.deserializedType === TransactionsType.RSS3_NFT_RECEIVE) {
            // receive nft
            item.from = item?.deserializedData?.estimatedChanges?.nfts?.[0]?.fromAddress || item.from;
        }
        else if (item.deserializedType === TransactionsType.RSS3_TOKEN_SEND || item.deserializedType == TransactionsType.TOKEN_SEND) {
            // send token
            item.to = item?.deserializedData?.estimatedChanges?.tokens?.[0]?.toAddress;
        }
        else if (item.deserializedType === TransactionsType.NATIVE_SEND || item.deserializedType === TransactionsType.RSS3_NATIVE_SEND) {
            // send native
            item.to = item?.deserializedData?.estimatedChanges?.natives?.[0]?.toAddress;
        }
        else if (item.deserializedType === TransactionsType.RSS3_NFT_SEND) {
            // send nft
            item.to = item?.deserializedData?.estimatedChanges?.nfts?.[0]?.toAddress;
        }
        else if (item.deserializedType === TransactionsType.RSS3_ERC_20_APPROVE) {
            // approve erc 20
            item.to = item?.deserializedData?.estimatedChanges?.tokens?.[0]?.toAddress;
        }
        let addressValue = '';
        if (actionName === 'in') {
            addressValue = `${this.t('from')}: ${subString(formWalletAddress(this.chainName, item.from || ''))}`;
        }
        else if (actionName === 'out') {
            addressValue = `${this.t('to')}: ${subString(formWalletAddress(this.chainName, item.to || ''))}`;
        }
        else {
            addressValue = `--`;
        }
        return {
            action: actionName,
            addressValue,
        };
    }
    async getTransactionsList(_tokenAddress = 'native', useCache = true) {
        const account = await this.getAddress();
        // @ts-ignore
        const { symbol: nativeSymbol } = window.__webWalletData.chainInfo || {};
        const id = md5(`${this.chainName}_${this.chainId}_${account}`.toLowerCase());
        // get cache data
        const cacheData = await database.tansactionInfoList.get(id);
        const cacheList = cacheData?.list || [];
        let list = await this.rpc.getTransactionsList(account, !!window.__webWalletData.isAA).then((res) => {
            return res.data.result;
        });
        if (list?.length) {
            const contacts = (await this.getUserAddress())?.filter((item) => !!item.alias);
            for (let i = 0; i < list.length; i++) {
                const item = list[i];
                // const { fees } = item;
                if (!item.deserializedData) {
                    item.deserializedData = {};
                }
                item.deserializedType = item.deserializedData.type || TransactionsType.UNKNOWN;
                const { amountChangeList, amountChangeNatives, amountChangeTokens, amountChangeNfts, totalBN } = await this.formatChangeAmountList(item, account);
                item.amountChangeNatives = amountChangeNatives;
                item.amountChangeTokens = amountChangeTokens;
                item.amountChangeNfts = amountChangeNfts;
                item.amountChangeList = amountChangeList;
                if (item.nonce && /^0x/.test(item.nonce)) {
                    item.nonceView = `#${parseInt(item.nonce, 16).toString()}`;
                }
                else if (item.nonce) {
                    item.nonceView = '#' + item.nonce;
                }
                // item.badge start
                if (item.deserializedType === TransactionsType.RSS3_SWAP) {
                    item.badge = item.deserializedData?.platform?.icon;
                }
                else if (
                // nft 不显示 badge
                item.deserializedType !== TransactionsType.RSS3_NFT_SEND &&
                    item.deserializedType !== TransactionsType.RSS3_NFT_RECEIVE &&
                    item.deserializedType !== TransactionsType.RSS3_NFT_APPROVE &&
                    item.deserializedType !== TransactionsType.RSS3_NFT_MINT &&
                    item.deserializedType !== TransactionsType.RSS3_NFT_BUY) {
                    item.badge = item.amountChangeList?.[0]?.icon;
                    const amountChange = Math.max(...[Number(item.amountChangeTokens?.[0]?.amountChange || 0), Number(item.amountChangeNatives?.[0]?.amountChange || 0)]);
                    if (!amountChange) {
                        item.badge = '';
                    }
                }
                // item.badge end
                item.functionName = item.deserializedData?.data?.function?.displayName || '';
                const { action, addressValue } = this.getTransactionsActionName(item, account);
                item.action = action;
                item.addressValue = addressValue;
                item.dateValue = dayjs((item?.timestamp || 0) * 1000).format('YYYY/MM/DD HH:mm:ss');
                item.total = totalBN.toString();
                item.totalView = tokenAmountFormat(totalBN);
                item.totalSymbol = nativeSymbol;
                item.pending = item.status == 0 || item.status == 1;
                item.statusName = item.status == 0 || item.status == 1 ? 'pending' : item.status == 4 ? 'failed' : 'confirmed';
                // item.you = item.from?.toLowerCase?.() === account?.toLowerCase?.() ? 'from' : item.to?.toLowerCase?.() === account?.toLowerCase?.() ? 'to' : 'none';
                const toAddress = formWalletAddress(this.chainName, item.to || '')?.toLowerCase();
                const fromAddress = formWalletAddress(this.chainName, item.from || '')?.toLowerCase();
                item.toNickname = contacts?.find?.((o) => o.userAddress?.toLowerCase() === toAddress)?.alias || '';
                item.fromNickname = contacts?.find?.((o) => o.userAddress?.toLowerCase() === fromAddress)?.alias || '';
                item.typeName = this.getTransactionsTypeName(item);
                Object.assign(list[i], {
                    ...item,
                    chainId: Number(item.chainId),
                    shortHash: item?.hash?.substring(0, 5) + '...' + item?.hash?.substring(item.hash.length - 5, item.hash.length),
                    date: dayjs((item?.timestamp || 0) * 1000).format(DateFormat),
                });
            }
        }
        // 如果 list 为空，使用缓存
        let newList = list?.length ? list : cacheList;
        // pending 和 completed 分别排序，然后合并 start
        let pendingList = newList.filter((item) => {
            return item.status === 0 || item.status === 1;
        });
        let confirmedList = newList.filter((item) => {
            return item.status !== 0 && item.status !== 1;
        });
        // 按照时间排序
        confirmedList = confirmedList.sort((a, b) => {
            return b.timestamp - a.timestamp;
        });
        // 按照时间排序
        pendingList = pendingList.sort((a, b) => {
            return b.timestamp - a.timestamp;
        });
        newList = [...pendingList, ...confirmedList];
        // pending 和 completed 分别排序，然后合并 end
        // 过滤 tokenAddress start
        // if (tokenAddress && tokenAddress !== 'native') {
        //   newList = newList.filter((item: any, index: number) => {
        //     return (
        //       item.to?.toLowerCase?.() === tokenAddress?.toLowerCase?.() ||
        //       item.from?.toLowerCase?.() === tokenAddress?.toLowerCase?.() ||
        //       !!item.deserializedData?.estimatedChanges?.tokens?.find((o: any) => {
        //         return o.address?.toLowerCase() === tokenAddress?.toLowerCase?.();
        //       })
        //     );
        //   });
        // }
        // 过滤 tokenAddress end
        // set cache data
        const newCacheData = {
            id,
            list: newList,
            updateTime: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
        };
        await database.tansactionInfoList.put(newCacheData);
        return newList;
    }
    async getNftByAddress(address, id) {
        // TODO
        return {
            address: '0x671655cD2189155665Da68c27eeE851832F76CB5',
            isSemiFungible: false,
            tokenId: '1',
            tokenBalance: '1',
            tokenURI: 'https://ikzttp.mypinata.cloud/ipfs/QmQFkLSQysj94s5GvTHPyzTxrawwtjgiiYS2TBLgrvw8CW/1',
            name: 'Azuki #1',
            symbol: 'NFT',
            image: 'https://ikzttp.mypinata.cloud/ipfs/QmYDvPAXtiJg7s8JdRBSLWdgSphQdac8j1YuQNNxcGE1hg/1.png',
            data: {
                name: 'Azuki #1',
                image: 'https://ikzttp.mypinata.cloud/ipfs/QmYDvPAXtiJg7s8JdRBSLWdgSphQdac8j1YuQNNxcGE1hg/1.png',
                attributes: [
                    {
                        value: 'Human',
                        trait_type: 'Type',
                    },
                    {
                        value: 'Pink Hairband',
                        trait_type: 'Hair',
                    },
                    {
                        value: 'White Qipao with Fur',
                        trait_type: 'Clothing',
                    },
                    {
                        value: 'Daydreaming',
                        trait_type: 'Eyes',
                    },
                    {
                        value: 'Lipstick',
                        trait_type: 'Mouth',
                    },
                    {
                        value: 'Gloves',
                        trait_type: 'Offhand',
                    },
                    {
                        value: 'Off White D',
                        trait_type: 'Background',
                    },
                ],
            },
            isScam: false,
        };
    }
    async getTokensByTokenAddresses(tokenAddressList) {
        let res = await this.rpc.getTokensByTokenAddresses(await this.getAddress(), tokenAddressList);
        let tokenData = await this.getAllToken();
        if (res && res.length) {
            res.map((item) => {
                let findData = tokenData.tokens.find((token) => token.address?.toLowerCase?.() === item.address?.toLowerCase?.()) || {};
                item.image = findData.logoURI || findData.image || item.logoURI || item.image || defaultTokenIcon;
                return item;
            });
        }
        return res;
    }
    // getEstimateGas
    async getEstimateGas(params) {
        return this.rpc.getEstimateGas(params);
    }
    // getSuggestedGasFees
    async getSuggestedGasFees() {
        return this.rpc.getSuggestedGasFees();
    }
    async addUserAddress(params) {
        let walletAddress = await this.getAddress();
        let userAddressData = await database.userAddressList.where({ userAddress: params.userAddress, walletAddress }).first();
        const shortUserAddress = params.userAddress.substring(0, 5) + '...' + params.userAddress.substring(params.userAddress.length - 5, params.userAddress.length);
        if (userAddressData || params.id) {
            return await database.userAddressList.put({ ...(userAddressData || {}), ...params, shortUserAddress, chainType: this.chainType, walletAddress });
        }
        else {
            const id = uuidv4();
            const { createIcon } = await import('@download/blockies');
            const imgSrc = createIcon({
                seed: params.userAddress,
                size: 10,
                scale: 10,
            }).toDataURL('image/png');
            return await database.userAddressList.put({ ...params, imgSrc, shortUserAddress, chainType: this.chainType, id, walletAddress });
        }
    }
    validateAddress(address) {
        return true;
    }
    async getUserAddress(params) {
        let walletAddress = await this.getAddress();
        let res = await database.userAddressList.where({ chainType: this.chainType, walletAddress }).toArray();
        if (this.chainName.toLowerCase() == 'tron') {
            res = res.filter((item) => item?.chainName?.toLowerCase() == 'tron');
        }
        else {
            res = res.filter((item) => item?.chainName?.toLowerCase() !== 'tron');
        }
        return res;
    }
    //deleteUserAddress
    async deleteUserAddress(id) {
        let addressInfo = await database.userAddressList.where({ id }).first();
        if (!addressInfo) {
            throw new Error(this.t('address_not_found'));
        }
        return await database.userAddressList.delete(id);
    }
    // addCustomToken
    async addCustomToken(params) {
        const id = md5(`${this.chainName}_${params.address}_${this.chainId}`);
        await database.customTokenList.put({
            id,
            ...params,
            chainName: this.chainName,
            chainId: this.chainId,
        });
        return database.customTokenList.where({ chainName: this.chainName, address: params.address }).toArray();
    }
    async updateCustomTokenByAddress(address, type) {
        const id = md5(`${this.chainName}_${address}_${this.chainId}`);
        let findData = await database.customTokenList.where({ id }).toArray();
        if (findData && findData.length) {
            if (type == 'delete') {
                findData[0].hidden = true;
            }
            else {
                findData[0].hidden = false;
            }
            await database.customTokenList.put(findData[0]);
            return true;
        }
        else {
            return true;
        }
    }
    async addCustomTokenList(params) {
        if (!params.address) {
            throw new Error(this.t('invalid_address'));
        }
        const id = md5(`${this.chainName}_${params.address}`);
        await database.customTokenList.put({
            ...params,
            chainName: this.chainName,
            id,
        });
        return true;
    }
    async getCustomTokenList() {
        return (await database.customTokenList.where({ chainId: this.chainId }).toArray()).filter((item) => item.hidden !== true);
    }
    async addImportTokenData(params) {
        const updateTime = dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss');
        params.chainName = params.chainName?.toLowerCase();
        params.chainNetwork = params.chainNetwork?.toLowerCase();
        params.chainId = Number(params.chainId);
        params.userAddress = params.userAddress?.toLowerCase();
        params.tokenAddress = params.tokenAddress?.toLowerCase();
        await database.importTokenList.put({
            ...params,
            updateTime,
        });
        return true;
    }
    async getImpoertTokenList(params) {
        const chainName = (params.chainName || this.chainName).toLowerCase();
        const chainId = Number(params.chainId || this.chainId);
        const userAddress = (await this.getAddress()).toLowerCase();
        params = {
            chainName,
            chainId,
            userAddress,
            ...params,
        };
        params.chainName = params.chainName?.toLowerCase?.();
        if (params.chainNetwork) {
            params.chainNetwork = params.chainNetwork?.toLowerCase();
        }
        else {
            const chain = this.getChainById(chainId);
            params.chainNetwork = chain?.network?.toLowerCase();
        }
        return await database.importTokenList.where(params).toArray();
    }
    async importNftData(chainInfo, nftInfo) {
        const { walletAddress, chainName, chainId } = chainInfo;
        const importNftInfo = {
            chainType: this.chainType.toLowerCase(),
            chainName: chainName.toLowerCase(),
            chainId,
            walletAddress: walletAddress?.toLowerCase(),
            nftAddress: nftInfo.address?.toLowerCase(),
            nftId: nftInfo.tokenId,
            isCustom: true,
            data: nftInfo,
            updateTime: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
        };
        await database.importNftList.put(importNftInfo);
        return true;
    }
    async getImportNftList(params) {
        const { walletAddress, chainName, chainId } = params;
        const queryParams = {
            chainType: this.chainType.toLowerCase(),
            chainId,
            chainName: chainName.toLowerCase(),
            walletAddress: walletAddress?.toLowerCase(),
        };
        return database.importNftList.where(queryParams).toArray();
    }
    async withdraw() {
        return new Promise((resolve, reject) => { });
    }
    getChainInfo() {
        return Object.entries(ParticleChains)?.find((item) => item[1].id == this.chainId && item[1].name.toLowerCase() == this.chainName.toLowerCase())?.[1] || {};
    }
    async getDecimalsByAddress(address, chainNetwork) {
        if (address === EVM_SWAP_NATIVE_ADDDRESS) {
            return this.getChainInfo()?.nativeCurrency?.decimals || 18;
        }
        let tokenData = await this.getAllToken();
        const cacheKey = await this.getTokenAndNftCacheKey();
        const tokenAndNFTs = await database.tokensAndNFTs.where({ id: cacheKey }).first();
        let decimals = [...(tokenAndNFTs?.tokens || []), ...tokenData.tokens].find((item) => item.address?.toLowerCase?.() == address?.toLowerCase?.())?.decimals || 0;
        if (!decimals) {
            const userAddress = (await this.getAddress())?.toLowerCase();
            let importTokenData = await this.getImpoertTokenList({ userAddress, chainNetwork });
            decimals = importTokenData.find((item) => item.tokenAddress?.toLowerCase?.() == address?.toLowerCase?.())?.metaData?.decimals || 0;
        }
        return Number(decimals ?? 18);
    }
    // removeImportTokenData
    async removeImportTokenData(data) {
        let params = {
            chainName: data.chainName,
            chainId: Number(data.chainId),
            tokenAddress: data.tokenAddress,
            chainNetwork: data.chainNetwork,
            userAddress: data.userAddress?.toLowerCase(),
        };
        return await database.importTokenList.where(params).delete();
    }
    // get token icon by address
    async getTokenIconByAddress(address) {
        if (address?.toLowerCase?.() == EVM_SWAP_NATIVE_ADDDRESS?.toLowerCase?.() || address?.toLowerCase?.() == 'native') {
            let chainList = getChainList(true, this.chainId, this.chainName);
            const { symbol, icon, chainId } = chainList.find((item) => item.selected) || {};
            return getNativeIcon(chains.getEVMChainInfoById(Number(chainId)));
        }
        let allTokenInfo = await database.allTokenInfo.where({ chainName: this.chainName, chainId: Number(this.chainId), chainType: this.chainType }).first();
        let tokenInfo = allTokenInfo?.tokens?.find?.((item) => item.address?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo && !!tokenInfo.logoURI) {
            return tokenInfo.logoURI;
        }
        const id = await this.getTokenAndNftCacheKey();
        const tokensAndNFTs = await database.tokensAndNFTs.where({ id }).first();
        tokenInfo = tokensAndNFTs?.tokens?.find((item) => item.address?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo) {
            return tokenInfo.image || defaultTokenIcon;
        }
        const userAddress = (await this.getAddress()).toLowerCase();
        let importTokenData = await this.getImpoertTokenList({ userAddress });
        tokenInfo = importTokenData.find((item) => item.tokenAddress?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo) {
            return tokenInfo.image || defaultTokenIcon;
        }
        return defaultTokenIcon;
    }
    async getQuote(params) {
        let { inputMint, outputMint, slippage, amount, isTry } = params;
        let inputMintDecimals = await this.getDecimalsByAddress(inputMint);
        let outputMintDecimals = 0;
        let selfAddress = await this.getAddress();
        let quoteRes = [];
        let quoteMessage = '';
        if (Number(amount) && outputMint) {
            outputMintDecimals = await this.getDecimalsByAddress(outputMint);
            // safe transfer
            // params.amount * Math.pow(10, inputMintDecimals);
            let _amount = new BigNumber(params.amount).multipliedBy(Math.pow(10, inputMintDecimals)).integerValue(1).toString(10);
            if (Number(_amount) <= 0) {
                throw new Error(this.t('invalid_amount'));
            }
            let res = null;
            try {
                res = await this.rpc.getQuote(selfAddress, { amount: _amount, inputMint, outputMint, slippage, provider: 'all', version: 'v2' });
                this.swapProvider = res?.provider || '';
            }
            catch (error) {
                console.log('getQuote', error);
                // @ts-ignore
                quoteMessage = error.message;
            }
            if (res && res.toTokenAmount) {
                quoteRes = [
                    {
                        ...res,
                        marketInfos: [{}],
                        fees: {
                            totalFeeAndDeposits: res.toTokenAmount,
                        },
                    },
                ];
            }
        }
        if (!quoteRes || quoteRes.length == 0) {
            quoteRes = [
                {
                    fees: {
                        totalFeeAndDeposits: 0,
                    },
                },
            ];
        }
        let routeData = quoteRes[0] || {};
        try {
            const id = await this.getTokenAndNftCacheKey();
            const tokensAndNFTs = (await database.tokensAndNFTs.where({ id }).first()) || {};
            let tokenBalance = tokensAndNFTs.tokens?.find((item) => item.address?.toLowerCase?.() == inputMint?.toLowerCase?.())?.amount || 0;
            if (inputMint?.toLowerCase?.() == EVM_SWAP_NATIVE_ADDDRESS?.toLowerCase?.()) {
                tokenBalance = Number(tokensAndNFTs.native);
            }
            // (tokenBalance - routeData.fees.totalFeeAndDeposits) / 10 ** inputMintDecimals;
            routeData.maxAmount = new BigNumber(tokenBalance).minus(routeData.fees.totalFeeAndDeposits).dividedBy(Math.pow(10, inputMintDecimals)).toString(10);
            routeData.maxAmount = routeData.maxAmount > 0 ? routeData.maxAmount : 0;
            let toTokenAmount = routeData.toTokenAmount;
            if (toTokenAmount) {
                // toTokenAmount / 10 ** outputMintDecimals
                routeData.outAmountValue = new BigNumber(toTokenAmount).dividedBy(Math.pow(10, outputMintDecimals)).toString(10);
                routeData.outAmountValue = numbro(routeData.outAmountValue > 0 ? routeData.outAmountValue : 0).format({
                    thousandSeparated: false,
                    trimMantissa: true,
                    mantissa: outputMintDecimals,
                });
            }
        }
        catch (error) {
            console.log('getTokenAndNftCacheKey', error);
        }
        // get symbol by address
        let inputSymbol = await this.getSymbolByAddress(inputMint);
        routeData.inputSymbol = inputSymbol;
        let outSymbol = outputMint ? await this.getSymbolByAddress(outputMint) : '';
        routeData.outSymbol = outSymbol;
        // get token icon by address
        let inputIcon = await this.getTokenIconByAddress(inputMint);
        routeData.inputIcon = inputIcon;
        if (outputMint) {
            let outIcon = await this.getTokenIconByAddress(outputMint);
            routeData.outIcon = outIcon;
        }
        if (Number(routeData.fromTokenAmount) && Number(routeData.toTokenAmount)) {
            // (1 / (routeData.fromTokenAmount / 10 ** inputMintDecimals)) * routeData.toTokenAmount / 10 ** outputMintDecimals;
            routeData.rateValue = new BigNumber(1)
                .dividedBy(new BigNumber(routeData.fromTokenAmount).dividedBy(Math.pow(10, inputMintDecimals)))
                .multipliedBy(routeData.toTokenAmount)
                .dividedBy(Math.pow(10, outputMintDecimals))
                .toString(10);
            routeData.rateValue = numbro(routeData.rateValue).format({
                trimMantissa: true,
                mantissa: 8,
            });
        }
        else {
            routeData.rateValue = '';
        }
        if (routeData.toTokenAmount) {
            routeData.outAmount = routeData.toTokenAmount;
        }
        if (routeData.estimatedGas) {
            const suggestedGasFees = await this.getSuggestedGasFees();
            const gasPrice = suggestedGasFees?.high?.maxFeePerGas;
            let gasLimit = '0x0';
            if (this.chainName.toLowerCase() !== 'tron') {
                gasLimit = routeData.estimatedGas;
            }
            if (gasPrice) {
                routeData.networkFees = gasFee({
                    gasPrice,
                    gasLimit,
                })?.maxFee;
            }
        }
        routeData.inputMintDecimals = inputMintDecimals;
        routeData.outputMintDecimals = outputMintDecimals;
        routeData.quoteMessage = quoteMessage;
        routeData.youPayAmount = isTry ? 0 : amount;
        console.log('routeData', routeData);
        return routeData;
    }
    async getSwapTransactions(routeData) {
        let selfAddress = await this.getAddress();
        return this.rpc.getSwapTransactions(selfAddress, { ...routeData, provider: this.swapProvider, version: 'v2' });
    }
    async getTransactionReceipt(params) {
        let res = null;
        do {
            res = await this.rpc.getTransactionReceipt(params);
            await new Promise((resolve) => setTimeout(resolve, 1000));
        } while (!res);
        return res;
    }
    async swapCheckApprove(params, provider = '') {
        const { address, inputMint, amount, transactionChainType, suggestedGasFees, version } = params || {};
        const transactions = [];
        const isAA = this.enableAccountAbstraction();
        if (inputMint?.toLowerCase?.() !== EVM_SWAP_NATIVE_ADDDRESS?.toLowerCase?.()) {
            let checkResult = await this.rpc.swapCheckApprove(address, {
                tokenAddress: inputMint,
                amount,
                provider: provider || this.swapProvider,
                version,
            });
            if (checkResult.approved === false) {
                const { data, to, value } = checkResult.tx;
                let gasLimit = '0x0';
                try {
                    if (this.chainName.toLowerCase() !== 'tron') {
                        gasLimit = await this.getEstimateGas({
                            value,
                            from: address,
                            data,
                            to,
                        });
                    }
                }
                catch (error) {
                    console.log('getEstimateGas', error);
                }
                let approveTx = {
                    data,
                    to,
                    value,
                    from: address,
                    gasLimit,
                    type: transactionChainType,
                    gasLevel: 'high',
                    maxFeePerGas: `0x${toWei(suggestedGasFees.medium.maxFeePerGas, 'gwei').toString(16)}`,
                    maxPriorityFeePerGas: `0x${toWei(suggestedGasFees.medium.maxPriorityFeePerGas, 'gwei').toString(16)}`,
                };
                if (approveTx.type === '0x0') {
                    approveTx.gasPrice = approveTx?.maxFeePerGas || '';
                    approveTx.gas = approveTx?.gasLimit || '';
                    delete approveTx.maxFeePerGas;
                    delete approveTx.maxPriorityFeePerGas;
                }
                console.log('approve params', approveTx);
                if (isAA) {
                    transactions.push(approveTx);
                }
                else {
                    const hash = await this.sendTransactionWrapper(approveTx);
                    await this.getTransactionReceipt(hash);
                }
            }
        }
        return transactions;
    }
    async swapCreateOrder(params) {
        const { inputMint, outputMint, amount, isTry } = params;
        const selfAddress = await this.getAddress();
        let routeData = {};
        let quoteMessage = '';
        let inputMintDecimals = await this.getDecimalsByAddress(inputMint);
        let outputMintDecimals = await this.getDecimalsByAddress(outputMint);
        if (Number(amount) && outputMint) {
            let _amount = new BigNumber(params.amount).multipliedBy(Math.pow(10, inputMintDecimals)).integerValue(1).toString(10);
            if (Number(_amount) <= 0) {
                throw new Error(this.t('invalid_amount'));
            }
            try {
                const res = await this.rpc.swapCreateOrder(selfAddress, {
                    fromTokenAddress: inputMint,
                    toTokenAddress: outputMint,
                    amount: _amount,
                    version: 'v2',
                });
                this.fusionProvider = res?.provider || '';
                routeData = { ...res };
                const { toTokenAmount } = res;
                routeData.outAmount = toTokenAmount;
                routeData.outAmountValue = new BigNumber(toTokenAmount).dividedBy(Math.pow(10, outputMintDecimals)).toString(10);
                routeData.outAmountValue = numbro(routeData.outAmountValue > 0 ? routeData.outAmountValue : 0).format({
                    thousandSeparated: false,
                    trimMantissa: true,
                    mantissa: outputMintDecimals,
                });
                if (Number(_amount) && Number(toTokenAmount)) {
                    routeData.rateValue = new BigNumber(1)
                        .dividedBy(new BigNumber(_amount).dividedBy(Math.pow(10, inputMintDecimals)))
                        .multipliedBy(toTokenAmount)
                        .dividedBy(Math.pow(10, outputMintDecimals))
                        .toString(10);
                    routeData.rateValue = numbro(routeData.rateValue).format({
                        trimMantissa: true,
                        mantissa: 8,
                    });
                }
                else {
                    routeData.rateValue = '';
                }
            }
            catch (error) {
                console.log('swapCreateOrder', error);
                // @ts-ignore
                quoteMessage = error?.message || '';
            }
        }
        routeData.marketInfos = [1];
        routeData.priceImpactPct = '';
        routeData.inputMintDecimals = inputMintDecimals;
        routeData.outputMintDecimals = outputMintDecimals;
        routeData.networkFees = '0';
        routeData.quoteMessage = quoteMessage;
        routeData.youPayAmount = isTry ? 0 : amount;
        return routeData;
    }
    async tokenSwapFusionMode(params) {
        const { inputMint, typedData } = params;
        const orderInfo = params.orderInfo || {};
        await switchChain(this.provider, this.chainName, this.chainId);
        const selfAddress = await this.getAddress();
        const inputMintDecimals = await this.getDecimalsByAddress(inputMint);
        const _amount = new BigNumber(params.amount)
            .multipliedBy(10 ** inputMintDecimals)
            .integerValue(1)
            .toString(10);
        const transactionChainType = await this.getTransactionChainType();
        const suggestedGasFees = await this.getSuggestedGasFees();
        const transactions = await this.swapCheckApprove({
            address: selfAddress,
            inputMint,
            amount: _amount,
            transactionChainType,
            suggestedGasFees,
            version: 'v2',
        }, this.fusionProvider);
        if (transactions.length) {
            const tx = await this.sendTransactionWrapper(transactions);
            console.log('Approve tx', tx);
        }
        orderInfo.signature = await this.provider.request({
            method: 'eth_signTypedData_v4',
            params: [selfAddress, JSON.stringify(typedData)],
        });
        await this.rpc.swapSubmitOrder(selfAddress, orderInfo);
        return orderInfo.orderHash;
    }
    async tokenSwap(params) {
        const { inputMint, outputMint, slippage, networkFees = 0 } = params;
        // switchChain
        await switchChain(this.provider, this.chainName, this.chainId);
        const selfAddress = await this.getAddress();
        const inputMintDecimals = await this.getDecimalsByAddress(inputMint);
        const balance = Number(await this.getBalance());
        const isAA = this.enableAccountAbstraction();
        const tokenSwapParams = {
            walletAddress: selfAddress,
            chainName: this.chainName,
            chainId: this.chainId,
            amount: Number(params.amount),
            newAmount: new BigNumber(balance).minus(networkFees * 2).toString(10),
            balance,
            networkFees,
            estimatedNetworkFees: networkFees * 2,
            inputMint: inputMint?.toLowerCase?.(),
            EVM_SWAP_NATIVE_ADDDRESS: EVM_SWAP_NATIVE_ADDDRESS?.toLowerCase?.(),
        };
        console.log('tokenSwapParams', tokenSwapParams);
        if (!isAA) {
            if (inputMint?.toLowerCase?.() === EVM_SWAP_NATIVE_ADDDRESS?.toLowerCase?.()) {
                if (Number(params.amount) > balance - networkFees * 2) {
                    // balance - networkFees * 2
                    params.amount = new BigNumber(balance).minus(networkFees * 2).toString(10);
                }
                if (Number(params.amount) <= 0) {
                    console.log('Amount is not enough');
                    throw new Error(this.t('network_fee_is_not_enough'));
                }
            }
            else if (networkFees > balance) {
                console.log('Network fee is not enough');
                throw new Error(this.t('network_fee_is_not_enough'));
            }
        }
        const _amount = new BigNumber(params.amount)
            .multipliedBy(10 ** inputMintDecimals)
            .integerValue(1)
            .toString(10);
        const transactionChainType = await this.getTransactionChainType();
        const suggestedGasFees = await this.getSuggestedGasFees();
        const transactions = await this.swapCheckApprove({
            address: selfAddress,
            inputMint,
            amount: _amount,
            transactionChainType,
            suggestedGasFees,
            version: 'v2',
        });
        let swapRes = await this.rpc.getSwapTransactions(selfAddress, {
            fromTokenAddress: inputMint,
            toTokenAddress: outputMint,
            amount: _amount,
            slippage,
            provider: this.swapProvider,
            version: 'v2',
        });
        let swapTx = {
            ...(swapRes.tx || {}),
            from: selfAddress,
            gasLevel: 'high',
            maxFeePerGas: `0x${toWei(suggestedGasFees.medium.maxFeePerGas, 'gwei').toString(16)}`,
            maxPriorityFeePerGas: `0x${toWei(suggestedGasFees.medium.maxPriorityFeePerGas, 'gwei').toString(16)}`,
            type: transactionChainType,
        };
        if (swapTx.type === '0x0') {
            swapTx.gasPrice = swapTx?.maxFeePerGas || '';
            swapTx.gas = swapTx?.gasLimit || '';
            delete swapTx.maxFeePerGas;
            delete swapTx.maxPriorityFeePerGas;
        }
        let swapTxHash;
        if (isAA) {
            transactions.push(swapTx);
            swapTxHash = await this.sendTransactionWrapper(transactions, false, [
                {
                    from: params.inputMint,
                    amount: params.amount,
                    decimals: await this.getDecimalsByAddress(params.inputMint),
                    type: TransactionType.SWAP,
                },
            ]);
        }
        else {
            swapTxHash = await this.sendTransactionWrapper(swapTx);
        }
        return [
            {
                txId: swapTxHash,
            },
        ];
    }
    async getSymbolByAddress(address) {
        if (address?.toLowerCase?.() == EVM_SWAP_NATIVE_ADDDRESS?.toLowerCase?.()) {
            let chainList = getChainList(true, this.chainId, this.chainName);
            return chainList.find((item) => item.selected)?.symbol || '';
        }
        let allTokenInfo = await database.allTokenInfo.where({ chainName: this.chainName, chainId: Number(this.chainId), chainType: this.chainType }).first();
        let tokenInfo = allTokenInfo?.tokens?.find?.((item) => item.address?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo) {
            return tokenInfo.symbol;
        }
        const id = await this.getTokenAndNftCacheKey();
        const tokensAndNFTs = await database.tokensAndNFTs.where({ id }).first();
        tokenInfo = tokensAndNFTs.tokens?.find((item) => item.address?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo) {
            return tokenInfo.symbol;
        }
        const userAddress = (await this.getAddress()).toLowerCase();
        let importTokenData = await this.getImpoertTokenList({ userAddress });
        tokenInfo = importTokenData.find((item) => item.tokenAddress?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo) {
            return tokenInfo.symbol;
        }
        return 'ETH';
    }
    getChainById(id) {
        const chains = getChainInfo();
        const chainName = Object.keys(chains).find((key) => {
            //@ts-ignore
            const info = chains[key];
            return info.chainType !== 'solana' && info.chainData[Number(id)];
        });
        if (!chainName) {
            return undefined;
        }
        //@ts-ignore
        const { symbol, icon, chainData } = chains[chainName];
        const network = chainData[Number(id)].network;
        const fullname = chainData[Number(id)].fullname;
        return {
            id: Number(id),
            name: chainName,
            network: network,
            symbol: symbol,
            icon: icon,
            fullname,
        };
    }
    async bridgeTokenFilterAndSort(chainName, chainId, tokens) {
        // 如果displayTokenAddresses有值，并且参数链一致，只显示displayTokenAddresses里面的token（暂不做）
        // 如果开启了隐藏可疑代币并且是主网，过滤掉_tokenList里面的可疑代币（暂不做）
        // 过滤隐藏调token，显示手动添加的token
        const importTokens = await this.getImpoertTokenList({
            chainName,
            chainId,
        });
        tokens = tokens.filter((item) => {
            const importToken = importTokens.find((importToken) => importToken.tokenAddress?.toLowerCase() === item.address?.toLowerCase());
            if (importToken?.isSelected === false || importToken?.isHide === true) {
                return false;
            }
            else {
                return true;
            }
        });
        // 排序：Native在首位，优先按 剩余按照持有的美元(price)价值进行排序，无价格按照持有数量(amount)进行排序；没数量的按照更新时间(updateTime)进行排序，新添加的在前
        tokens = cloneDeep(tokens)
            .sort((a, b) => {
            a.price = Number(a.price) || 0;
            b.price = Number(b.price) || 0;
            a.amount = Number(a.amount) || 0;
            b.amount = Number(b.amount) || 0;
            if (a.address?.toLowerCase?.() == 'native' || a.address?.toLowerCase?.() == EVM_SWAP_NATIVE_ADDDRESS?.toLowerCase?.()) {
                return -1;
            }
            if (b.address?.toLowerCase?.() == 'native' || b.address?.toLowerCase?.() == EVM_SWAP_NATIVE_ADDDRESS?.toLowerCase?.()) {
                return 1;
            }
            if (a.price && b.price) {
                return b.price - a.price;
            }
            if (a.price && !b.price) {
                return -1;
            }
            if (!a.price && b.price) {
                return 1;
            }
            if (a.amount && b.amount) {
                return b.amount - a.amount;
            }
            if (a.amount && !b.amount) {
                return -1;
            }
            if (!a.amount && b.amount) {
                return 1;
            }
            if (a.updateTime && b.updateTime) {
                return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
            }
            if (a.updateTime && !b.updateTime) {
                return -1;
            }
            if (!a.updateTime && b.updateTime) {
                return 1;
            }
            return 0;
        })
            .map((item) => item.address)
            .map((address) => {
            return tokens.find((item) => item.address == address);
        });
        // 如果 webWalletData.priorityTokenAddresses 里面有值，就把 priorityTokenAddresses 里面的 token 放到最前面,但是 native token 一定要放在最前面
        return tokens;
    }
    /**
     * 获取 Bridge 支持的链列表
     */
    async getBridgeSupportedTokens(chainId) {
        const chainInfo = chains.getEVMChainInfoById(chainId);
        let tokens = await this.rpc.getBridgeSupportedTokens(chainId);
        const address = await this.getAddress();
        const chainName = chainInfo?.name.toLowerCase();
        const tokenListKey = getTokenListCacheKey(address, chainName, chainId);
        await new Promise((resolve) => {
            const timer = setInterval(() => {
                if (window.storage.getItem(tokenListKey) !== null) {
                    clearInterval(timer);
                    resolve(null);
                }
            }, 10);
        });
        const indexTotkenList = getCacheTokenList(address, chainName, chainId);
        // 添加对应price 和 amount 设置native icon
        tokens = tokens.map((item) => {
            let itemAddress = item.address?.toLowerCase();
            if (itemAddress == EVM_SWAP_NATIVE_ADDDRESS.toLowerCase()) {
                itemAddress = 'native';
            }
            const targetToken = indexTotkenList.find((indexItem) => indexItem.address?.toLowerCase() === item.address?.toLowerCase() || indexItem.address?.toLowerCase() === itemAddress);
            if (targetToken) {
                item.amount = (targetToken?.amount || '').toString();
                item.doubleUnitPrice = targetToken?.doubleUnitPrice || '';
                item.price = targetToken?.oPrice || 0;
                item.uPrice = targetToken?.uPrice || 0;
                item.unitPrice = (targetToken?.unitPrice || '').toString();
            }
            if (item.address?.toLowerCase() === EVM_SWAP_NATIVE_ADDDRESS.toLowerCase()) {
                const chain = chains.getEVMChainInfoById(chainId);
                item.symbol = chain?.nativeCurrency?.symbol || item.symbol;
                item.logoURI = getNativeIcon(chain);
            }
            return item;
        });
        return this.bridgeTokenFilterAndSort(chainInfo?.name.toLowerCase(), chainId, tokens);
    }
    /**
     * 获取 Bridge Quote
     * @param params
     * @returns
     */
    async getBridgeQuote(params) {
        const walletAddress = await this.getAddress();
        const quote = await this.rpc.bridgeGetQuote(walletAddress, {
            ...params,
            toAddress: walletAddress,
        });
        if (quote.estimatedGas) {
            const suggestedGasFees = await this.getSuggestedGasFees();
            const gasPrice = suggestedGasFees?.high?.maxFeePerGas;
            let gasLimit = '0x0';
            if (this.chainName.toLowerCase() !== 'tron') {
                gasLimit = quote.estimatedGas;
            }
            if (gasPrice) {
                quote.networkFees = gasFee({
                    gasPrice,
                    gasLimit,
                })?.maxFee;
            }
            const decimals = getParticleChainInfo(this.chainName, Number(this.chainId))?.decimals ?? 18;
            if (quote?.tx?.value) {
                quote.networkFees = new BigNumber(quote?.tx?.value)
                    .dividedBy(Math.pow(10, decimals))
                    .plus(new BigNumber(quote.networkFees || 0))
                    .minus(new BigNumber(params.amount).dividedBy(Math.pow(10, decimals)))
                    .toString(10);
            }
        }
        return quote;
    }
    async bridgeApproveToken(params, walletAccount) {
        const { data, to, value } = params;
        const transactions = [];
        const suggestedGasFees = await this.getSuggestedGasFees();
        let gasLimit = '';
        const isAA = this.enableAccountAbstraction();
        try {
            if (this.chainName.toLowerCase() !== 'tron') {
                gasLimit = await this.getEstimateGas({
                    value,
                    from: walletAccount,
                    data,
                    to,
                });
            }
        }
        catch (error) {
            console.log('getEstimateGas error', error);
        }
        const maxFeePerGas = `0x${toWei(suggestedGasFees.medium.maxFeePerGas, 'gwei').toString(16)}`;
        const maxPriorityFeePerGas = `0x${toWei(suggestedGasFees.medium.maxPriorityFeePerGas, 'gwei').toString(16)}`;
        const transactionParams = {
            from: walletAccount,
            type: this.transactionChainType,
            gasLevel: 'high',
            maxFeePerGas,
            maxPriorityFeePerGas,
            chainId: this.chainId,
            data,
            to,
            value,
        };
        if (gasLimit) {
            transactionParams.gasLimit = gasLimit;
        }
        if (transactionParams.type === '0x0') {
            transactionParams.gasPrice = transactionParams?.maxFeePerGas || '';
            transactionParams.gas = transactionParams?.gasLimit || '';
            delete transactionParams.maxFeePerGas;
            delete transactionParams.maxPriorityFeePerGas;
        }
        console.log('transactionParams', transactionParams);
        if (isAA) {
            transactions.push(transactionParams);
        }
        else {
            const transactionHash = await this.sendTransactionWrapper(transactionParams);
            await this.getTransactionReceipt(transactionHash);
        }
        return transactions;
    }
    /**
     * Bridge 跨链转账
     * https://minijoy.feishu.cn/docx/doxcnE2eIXKOyUYf5dVEsQRvfNg
     * @param params
     * @returns
     */
    async tokenBridge(quote) {
        const walletAddress = await this.getAddress();
        await switchChain(this.provider, this.chainName, this.chainId);
        await this.getTransactionChainType();
        const isAA = this.enableAccountAbstraction();
        if (isAA) {
            // 关于AA Bridge：
            // Bridge Native：
            // native余额必须大于  (quote.tx.value + BridgeAmount)
            // Bridge Token:
            // native 余额必须大于 quote.tx.value
            const chainInfo = chains.getEVMChainInfoById(Number(this.chainId));
            const nativeBalanceBN = new BigNumber((await this.getBalance()) || 0);
            let minReqAmountBN = new BigNumber(quote.tx.value).dividedBy(10 ** (chainInfo?.nativeCurrency?.decimals || 0));
            if (quote.fromToken.address?.toLowerCase() === EVM_SWAP_NATIVE_ADDDRESS.toLowerCase()) {
                minReqAmountBN = minReqAmountBN.plus(new BigNumber(quote.fromTokenAmount || 0).dividedBy(10 ** (chainInfo?.nativeCurrency?.decimals || 0)));
            }
            console.log('nativeBalance', nativeBalanceBN.toNumber());
            console.log('minReqAmount', minReqAmountBN.toNumber());
            if (quote.tx.value && minReqAmountBN.gt(nativeBalanceBN)) {
                throw new Error(this.t('insufficient_balance'));
            }
        }
        const bridgeCheckApproveParams = {
            tokenAddress: quote.fromToken.address,
            amount: quote.fromTokenAmount,
            provider: 'lifi', // bridge 时需设置为 lifi，其他需设置为 particle_swap_getQuote 返回的 provider
        };
        const checkResult = await this.rpc.bridgeCheckApprove(walletAddress, bridgeCheckApproveParams);
        const transactions = [];
        if (checkResult.approved === false) {
            transactions.push(...(await this.bridgeApproveToken(checkResult.tx, walletAddress)));
        }
        const { data, value, to } = quote.tx || {};
        const suggestedGasFees = await this.getSuggestedGasFees();
        const maxFeePerGas = `0x${toWei(suggestedGasFees.medium.maxFeePerGas, 'gwei').toString(16)}`;
        const maxPriorityFeePerGas = `0x${toWei(suggestedGasFees.medium.maxPriorityFeePerGas, 'gwei').toString(16)}`;
        let transactionParams = {
            data,
            value,
            to,
            from: walletAddress,
            type: this.transactionChainType,
            gasLevel: 'high',
            maxFeePerGas,
            maxPriorityFeePerGas,
        };
        if (!this.enableAccountAbstraction()) {
            transactionParams.gasLimit = await this.getEstimateGas({
                value,
                from: walletAddress,
                data,
                to,
            });
        }
        if (transactionParams.type === '0x0') {
            transactionParams.gasPrice = transactionParams?.maxFeePerGas || '';
            transactionParams.gas = transactionParams?.gasLimit || '';
            delete transactionParams.maxFeePerGas;
            delete transactionParams.maxPriorityFeePerGas;
        }
        console.log('tokenBridge transactionParams', transactionParams);
        let transactionHash = '';
        if (isAA) {
            transactions.push(transactionParams);
            transactionHash = await this.sendTransactionWrapper(transactions, false, [
                {
                    from: quote.fromToken.address,
                    amount: new BigNumber(quote.fromTokenAmount).dividedBy(10 ** quote?.fromToken?.decimals || 0).toNumber(),
                    decimals: quote.fromToken.decimals,
                    type: TransactionType.BRIDGE,
                },
            ]);
        }
        else {
            transactionHash = await this.sendTransactionWrapper(transactionParams);
        }
        console.log('tokenBridge transactionHash', transactionHash);
        // 目的：上报 hash
        try {
            const res = await this.rpc.bridgeStatus(transactionHash);
            console.log('bridgeStatus', res);
        }
        catch (error) {
            console.log('bridgeStatus err', error);
        }
        // 添加
        try {
            const { toChainId, toToken } = quote;
            if (toToken.address?.toLowerCase() !== 'native' && toToken.address?.toLowerCase() !== EVM_SWAP_NATIVE_ADDDRESS?.toLowerCase()) {
                const chainInfo = chains.getEVMChainInfoById(Number(toChainId));
                const chainName = chainInfo?.name || '';
                const walletAddress = await this.getAddress();
                const metaDatas = await this.rpc.getTokensByTokenAddresses(walletAddress, [toToken.address?.toLowerCase()], toChainId);
                const data = {
                    chainName,
                    chainId: toChainId,
                    userAddress: walletAddress,
                    tokenAddress: toToken.address?.toLowerCase(),
                    chainNetwork: chainInfo?.network || '',
                    isSelected: true,
                    isHide: false,
                    image: toToken.logoURI,
                    isCustom: true,
                    name: toToken.name,
                    symbol: toToken.symbol,
                    updateTime: '',
                    metaData: metaDatas?.[0] || {},
                };
                await this.addImportTokenData(data);
            }
        }
        catch (error) {
            console.log('addImportTokenData', error);
        }
        return transactionHash;
    }
    async bridgeStatus(transactionHash) {
        return this.rpc.bridgeStatus(transactionHash);
    }
    async getBridgeHistoryList(status) {
        const walletAddress = await this.getAddress();
        let list = await this.rpc.bridgeHistoryList(walletAddress, status);
        list = list.map((bridgeHistory) => {
            if (bridgeHistory.createdAt) {
                bridgeHistory.createdDate = dayjs(bridgeHistory.createdAt).format('YYYY/MM/DD HH:mm:ss');
            }
            bridgeHistory.statusType = bridgeHistory.status === 'DONE' ? 'confirmed' : bridgeHistory.status === 'FAILED' ? 'failed' : 'pending';
            const fromChainInfo = chains.getEVMChainInfoById(bridgeHistory.fromToken.chainId || Number(bridgeHistory.fromChainIcon));
            const toChainInfo = chains.getEVMChainInfoById(bridgeHistory?.toToken?.chainId || Number(bridgeHistory?.toChainIcon));
            bridgeHistory.fromChainIcon = chains.getEVMChainInfoById(bridgeHistory.fromToken.chainId)?.icon || '';
            bridgeHistory.toChainIcon = chains.getEVMChainInfoById(bridgeHistory?.toToken?.chainId || Number(bridgeHistory?.toChainIcon))?.icon || '';
            if (bridgeHistory.fromToken.address?.toLowerCase() === EVM_SWAP_NATIVE_ADDDRESS) {
                bridgeHistory.fromToken.decimals = fromChainInfo?.nativeCurrency.decimals || 0;
                bridgeHistory.fromToken.symbol = fromChainInfo?.nativeCurrency.symbol || '';
                bridgeHistory.fromNativeIcon = getNativeIcon(chains.getEVMChainInfoById(Number(bridgeHistory?.fromToken?.chainId)));
            }
            if (bridgeHistory?.toToken?.address?.toLowerCase() === EVM_SWAP_NATIVE_ADDDRESS) {
                bridgeHistory.toToken.decimals = toChainInfo?.nativeCurrency.decimals || 0;
                bridgeHistory.toToken.symbol = toChainInfo?.nativeCurrency.symbol || '';
                bridgeHistory.toNativeIcon = getNativeIcon(chains.getEVMChainInfoById(Number(bridgeHistory?.toToken?.chainId)));
            }
            bridgeHistory.fromTokenAmount = toNonExponential(new BigNumber(bridgeHistory.fromTokenAmount).dividedBy(10 ** bridgeHistory.fromToken.decimals).toNumber(), bridgeHistory.fromToken.decimals);
            bridgeHistory.oToTokenAmount = bridgeHistory.toTokenAmount;
            bridgeHistory.toTokenAmount = !!bridgeHistory.toTokenAmount
                ? toNonExponential(new BigNumber(bridgeHistory.toTokenAmount).dividedBy(new BigNumber(10).pow(bridgeHistory?.toToken?.decimals || 0)).toNumber(), bridgeHistory?.toToken?.decimals || 6)
                : '';
            return bridgeHistory;
        });
        return list;
    }
    async deployWalletContract() {
        const res = await this.sendTransactionWrapper({
            to: '0x0000000000000000000000000000000000000000',
            data: '0x',
            value: '0x0',
        });
        console.log('deployWalletContract', res);
    }
    /**
     * 根据btc地址获取evm地址
     * @param btcAddress
     * @returns
     */
    async getEvmAddressByBtcAddress(btcAddress) {
        const BTCConnectVersion = window.__webWalletData?.accountAbstraction?.version || '1.0.0';
        const cacheKey = `btcAddress_${this.chainId}_${BTCConnectVersion}_${btcAddress.toLowerCase()}`;
        const cacheAddress = sessionStorage.getItem(cacheKey);
        if (cacheAddress) {
            return cacheAddress;
        }
        const res = await this.rpc.getBTCAccountsByAddress({ btcAddress });
        const evmAddress = res?.find((item) => item?.version === BTCConnectVersion)?.aaAddress || '';
        sessionStorage.setItem(cacheKey, evmAddress);
        return evmAddress;
    }
    /**
     * 根据evm地址获取btc地址
     * @param evmAddress
     */
    async getBtcAddressByEvmAddress(evmAddress) {
        const BTCConnectVersion = window.__webWalletData?.accountAbstraction?.version || '1.0.0';
        const cacheKey = `evmAddress_${this.chainId}_${BTCConnectVersion}_${evmAddress.toLowerCase()}`;
        const cacheAddress = sessionStorage.getItem(cacheKey);
        if (cacheAddress) {
            return cacheAddress;
        }
        const res = await this.rpc.getBTCAccountsByAddress({ address: toChecksumAddress(evmAddress) });
        const btcAddress = res?.find((item) => item?.version === BTCConnectVersion)?.btcAddress || '';
        sessionStorage.setItem(cacheKey, btcAddress);
        return btcAddress;
    }
}
//# sourceMappingURL=evm-service.js.map